import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getApiUrl } from './config/api';
import { InteractiveSteps } from './components/InteractiveSteps';
import { MarkdownRenderer } from './components/MarkdownRenderer';
import { PromptGalleryModal } from './components/PromptGalleryModal';
import { useConfirmDialog } from './components/ConfirmDialog';
import { useAuth } from 'react-oidc-context';
import { isTeamsHost } from './utils/teamsHost';
import PdfViewer from './components/PdfViewer';
// import { useSpeechRecognition } from './hooks/useSpeechRecognition';
import './styles/ChatBot.css';
import extarctHyperautomationLabel from './utils/extarctHyperautomationLabel';
import { openExternalLink } from './utils/teamsSdk';
import { clearTeamsAuthInProgress } from './utils/teamsAuthFlow';
import TeamsDebugPanel from './components/TeamsDebugPanel';

/** OIDC profile fields for RBAC + AgentCore actorId (Phase 3). */
function buildCwUserAuthDetails(user: { profile?: Record<string, unknown> } | null | undefined): Record<string, string> {
  if (!user?.profile) return {};
  const p = user.profile;
  const out: Record<string, string> = {};
  const take = (key: string) => {
    const v = p[key];
    if (typeof v === 'string' && v.length > 0) {
      out[key] = v;
    }
  };
  take('sub');
  take('email');
  take('preferred_username');
  take('name');
  return out;
}

/** When POST /chat returns 4xx/5xx with JSON `{ reply }`, surface that text (AgentCore/Bedrock errors). */
async function chatErrorReplyFromResponse(response: Response): Promise<string> {
  const fallback = `Request failed (${response.status}).`;
  try {
    const ct = response.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const errData = await response.json();
      if (typeof errData.reply === 'string' && errData.reply.trim()) {
        return errData.reply.trim();
      }
    }
  } catch {
    /* ignore parse errors */
  }
  return fallback;
}

// Custom dropdown for Teams iframe compatibility (native <select> loses focus in Teams)
// Defined outside ChatBot for stable component identity
const CustomSelect: React.FC<{
  value: string;
  options: { value: string; label: string; disabled?: boolean }[];
  onChange: (value: string) => void;
  theme: 'dark' | 'light';
}> = ({ value, options, onChange, theme }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find(o => o.value === value);
  const inputBg = theme === 'dark' ? 'bg-gray-700' : 'bg-white';
  const borderColor = theme === 'dark' ? 'border-gray-600' : 'border-gray-300';
  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-400';
  const textTertiary = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const dropdownBg = theme === 'dark' ? 'bg-gray-700' : 'bg-white';
  const hoverBg = theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onMouseDown={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`w-full ${inputBg} border ${borderColor} rounded-lg px-3 py-2 ${textColor} text-sm text-left flex items-center justify-between`}
      >
        <span>{selected?.label || value}</span>
        <svg className={`w-4 h-4 ${textTertiary} transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className={`absolute z-[10000] mt-1 w-full ${dropdownBg} border ${borderColor} rounded-lg shadow-lg overflow-hidden`}>
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              disabled={opt.disabled}
              onMouseDown={(e) => {
                e.stopPropagation();
                if (!opt.disabled) { onChange(opt.value); setOpen(false); }
              }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${opt.disabled
                ? `${textMuted} cursor-not-allowed`
                : opt.value === value
                  ? 'bg-cyan-600/20 text-cyan-400'
                  : `${textColor} ${hoverBg}`
                }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// AIAssistantLogo component using inline SVG
const AIAssistantLogo = ({ size = 24 }: { size?: number }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="cbStarGrad" x1="0" y1="8" x2="16" y2="8" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#38C5F5" />
          <stop offset="100%" stopColor="#B0E354" />
        </linearGradient>
      </defs>
      <path d="M12.4997 9.94253C12.067 10.2382 11.4938 10.1864 11.1249 9.82229C9.71614 8.45662 7.6394 8.06575 5.83061 8.82587C5.35452 9.03108 4.80165 8.87091 4.50598 8.43816C4.21039 8.00552 4.26212 7.43231 4.62614 7.06337C5.99199 5.65465 6.38273 3.57777 5.62256 1.76884C5.4175 1.29281 5.57768 0.740008 6.01035 0.444398C6.44299 0.148806 7.01623 0.200517 7.38516 0.564588C8.794 1.93032 10.8709 2.32127 12.6798 1.56095C13.1558 1.35594 13.7085 1.51616 14.0041 1.94877C14.2997 2.38141 14.248 2.95456 13.884 3.32349C12.5181 4.73222 12.1273 6.80911 12.8875 8.61803C13.0926 9.09407 12.9324 9.64692 12.4997 9.94253Z" fill="url(#cbStarGrad)" />
      <path d="M1.69207 11.073C1.40787 10.5905 2.00003 10.064 2.44591 10.4028C3.44203 11.1439 4.78952 11.223 5.86558 10.6037C6.34796 10.3196 6.87439 10.9118 6.53574 11.3576C5.79474 12.3537 5.71544 13.7012 6.33481 14.7772C6.619 15.2597 6.02684 15.7862 5.58097 15.4474C4.58484 14.7063 3.23736 14.6272 2.16129 15.2465C1.67892 15.5306 1.15248 14.9384 1.49113 14.4926C2.23213 13.4965 2.31143 12.149 1.69207 11.073Z" fill="url(#cbStarGrad)" />
    </svg>
  );
};

type RagTitleUrlLink = { title: string; url: string };
type ReferenceLinkPayload = string | Record<string, unknown>;

type SseChatResult = {
  content: string;
  links: ReferenceLinkPayload[];
  pages: number[];
  usage: unknown;
  model?: string;
  langfuseTraceId?: string;
};

/** Read /api/chat SSE until result or error; captures structured reference links when present. */
async function readSseChatStream(
  body: ReadableStream<Uint8Array>,
  onStatus?: (status: string) => void,
): Promise<SseChatResult> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let content = '';
  let links: ReferenceLinkPayload[] = [];
  let pages: number[] = [];
  let usage: unknown = null;
  let model: string | undefined;
  let langfuseTraceId: string | undefined;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const event = JSON.parse(line.slice(6));
        if (event.type === 'status' && onStatus) {
          onStatus(event.content);
        } else if (event.type === 'result') {
          content = event.content || '';
          usage = event.usage;
          model = event.model;
          if (typeof event.langfuse_trace_id === 'string' && event.langfuse_trace_id.trim()) {
            langfuseTraceId = event.langfuse_trace_id.trim();
          }
          if (Array.isArray(event.links)) links = event.links;
          if (Array.isArray(event.pages)) pages = event.pages;
        } else if (event.type === 'error') {
          content = `Error: ${event.content}`;
        }
      } catch {
        /* skip unparseable SSE lines */
      }
    }
  }

  return { content, links, pages, usage, model, langfuseTraceId };
}

function excelFieldsFromReply(data: {
  excel_base64?: unknown;
  excel_filename?: unknown;
}): { excelBase64?: string; excelFilename?: string } {
  const raw = typeof data.excel_base64 === 'string' ? data.excel_base64.replace(/\s/g, '') : '';
  if (!raw || !/^[A-Za-z0-9+/]+={0,2}$/.test(raw)) return {};
  const requested = typeof data.excel_filename === 'string' ? data.excel_filename.trim() : '';
  const excelFilename = /^[\w.-]+\.xlsx$/.test(requested) ? requested : 'zoominfo-contacts.xlsx';
  return { excelBase64: raw, excelFilename };
}

function downloadExcelAttachment(base64: string, filename: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// Message interface
interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  feedback?: 'positive' | 'negative';
  isStreaming?: boolean;
  agent?: string;
  interactive_steps?: {
    title: string;
    steps: Array<{
      id: string;
      title: string;
      description: string;
      required: boolean;
    }>;
  };
  /** RAG / research source links (plain URLs, title+url dicts, or PSA-KB stringified dicts) */
  links?: ReferenceLinkPayload[];
  langfuseTraceId?: string;
  /** Parallel page numbers for PDF source links (NOC); index-aligned with `links` */
  pages?: number[];
  /** ZoomInfo enrich workbook. Present only after a contact enrich. */
  excelBase64?: string;
  excelFilename?: string;
}

// Chat session interface
interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  lastActive: Date;
}

// Agent interface
interface Agent {
  id: string;
  name: string;
  description: string;
  systemPrompt: string;
  capabilities: string[];
  icon: ({ className }: { className?: string }) => JSX.Element;
}

// Settings interface
interface Settings {
  theme: 'dark' | 'light';
  fontSize: 'small' | 'medium' | 'large';
  notifications: boolean;
  autoSave: boolean;
  streamingEnabled: boolean;
  temperature: number;
  // Chat Behavior
  showMessageTimestamps: boolean;
  startWithCollapsedSidebar: boolean;
  startInFullscreenMode: boolean;
  // Data Management
  chatRetentionDays: number;
}

type ClientChatConfig = {
  spend_limit?: number;
  main_chat_model_display?: string;
  azure_openai_deployment?: string;
  use_agentcore_main_chat?: boolean;
  agentcore_main_chat_model_id?: string;
  rbac_enforced?: boolean;
  prompts_table_configured?: boolean;
};

// AIHS use cases id
const aihs_use_cases = ['psa_kb', 'hyperautomation', 'noc_kb'];
// Function to get gradient colors for each agent
const SELECTED_AGENT_STORAGE_KEY = 'sidekick-selected-agent';

const getAgentGradient = (agentId: string): string => {
  const gradients: { [key: string]: string } = {
    'gpt': 'bg-gradient-to-br from-emerald-700 to-teal-800',
    'docs': 'bg-gradient-to-br from-blue-500 to-indigo-600',
    'research': 'bg-gradient-to-br from-amber-500 to-orange-600',
    'perplexity_research': 'bg-gradient-to-br from-violet-600 to-fuchsia-600',
    'm365_copilot': 'bg-gradient-to-br from-sky-600 to-blue-800',
    'saleswise': 'bg-gradient-to-br from-orange-500 to-red-600',
    'noc_kb': 'bg-gradient-to-br from-yellow-500 to-amber-600',
    'psa_kb': 'bg-gradient-to-br from-indigo-500 to-purple-600',
    'hyperautomation': 'bg-gradient-to-br from-green-500 to-lime-600',
    'prospecting': 'bg-gradient-to-br from-cyan-600 to-blue-700',
  };

  return gradients[agentId] || 'bg-gradient-to-br from-gray-500 to-gray-600';
};

// Available agents
const AVAILABLE_AGENTS: Agent[] = [
  {
    id: 'gpt',
    name: 'ConnectWise GPT',
    description: 'General-purpose assistant—use ConnectWise University for product how-tos, Web Research or Perplexity Research for internet-backed answers',
    systemPrompt:
      'You are ConnectWise GPT, a secure general-purpose assistant for ConnectWise employees. For ConnectWise product information, official how-tos, or documented procedures, tell the user to select the ConnectWise University agent. For answers that need web search and cited public sources, tell them to select the Web Research or Perplexity Research agent. You can also help colleagues request a Cursor usage-limit increase when they ask about Cursor limits or running out of Cursor usage.',
    capabilities: ['General Q&A', 'Writing Assistance', 'Analysis', 'Brainstorming', 'Cursor usage-limit requests'],
    icon: ({ className }: { className?: string }) => (
      <svg className={className} width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <defs><linearGradient id="cbStarGrad" x1="0" y1="8" x2="16" y2="8" gradientUnits="userSpaceOnUse"><stop offset="0%" stopColor="#38C5F5" /><stop offset="100%" stopColor="#B0E354" /></linearGradient></defs>
        <path d="M12.4997 9.94253C12.067 10.2382 11.4938 10.1864 11.1249 9.82229C9.71614 8.45662 7.6394 8.06575 5.83061 8.82587C5.35452 9.03108 4.80165 8.87091 4.50598 8.43816C4.21039 8.00552 4.26212 7.43231 4.62614 7.06337C5.99199 5.65465 6.38273 3.57777 5.62256 1.76884C5.4175 1.29281 5.57768 0.740008 6.01035 0.444398C6.44299 0.148806 7.01623 0.200517 7.38516 0.564588C8.794 1.93032 10.8709 2.32127 12.6798 1.56095C13.1558 1.35594 13.7085 1.51616 14.0041 1.94877C14.2997 2.38141 14.248 2.95456 13.884 3.32349C12.5181 4.73222 12.1273 6.80911 12.8875 8.61803C13.0926 9.09407 12.9324 9.64692 12.4997 9.94253Z" fill="url(#cbStarGrad)" />
        <path d="M1.69207 11.073C1.40787 10.5905 2.00003 10.064 2.44591 10.4028C3.44203 11.1439 4.78952 11.223 5.86558 10.6037C6.34796 10.3196 6.87439 10.9118 6.53574 11.3576C5.79474 12.3537 5.71544 13.7012 6.33481 14.7772C6.619 15.2597 6.02684 15.7862 5.58097 15.4474C4.58484 14.7063 3.23736 14.6272 2.16129 15.2465C1.67892 15.5306 1.15248 14.9384 1.49113 14.4926C2.23213 13.4965 2.31143 12.149 1.69207 11.073Z" fill="url(#cbStarGrad)" />
      </svg>
    )
  },
  {
    id: 'noc_kb',
    name: 'NOC Assist KB',
    description: 'NOC knowledge base assistant — direct answers sourced from the managed NOC knowledge base with citations',
    systemPrompt:
      'You are NOC Assist KB, a specialized assistant that answers questions using ConnectWise\'s managed NOC knowledge base. Ground every answer in retrieved knowledge base excerpts and cite sources when available. If the knowledge base does not contain enough information, say so clearly rather than guessing.',
    capabilities: ['Knowledge Base Retrieval', 'Runbook Lookup', 'Cited Answers'],
    icon: ({ className }: { className?: string }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
      </svg>
    ),
  },
  {
    id: 'psa_kb',
    name: 'SalesForce KB',
    description: 'ConnectWise SalesForce knowledge base — procedures, configuration, and documented workflows',
    systemPrompt:
      'You are SalesForce KB, a specialized assistant for ConnectWise Manage SalesForce documentation and knowledge base lookups. Provide clear, actionable answers grounded in SalesForce documentation.',
    capabilities: ['SalesForce Procedures', 'Configuration Guidance', 'Knowledge Base Lookup', 'Workflow Help', 'SalesForce Documentation Search'],
    icon: ({ className }: { className?: string }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    id: 'hyperautomation',
    name: 'Hyper Automation',
    description: 'Hyper automation assistant — intelligent process automation, workflow orchestration, and RPA knowledge base lookups',
    systemPrompt:
      'You are Hyperautomation, a specialized assistant for hyperautomation and intelligent process automation. You help with RPA design, workflow orchestration, process discovery, automation opportunity identification, and hyperautomation knowledge base lookups. Provide clear, actionable guidance suitable for automation engineers and business analysts.',
    capabilities: ['Process Automation', 'RPA Guidance', 'Workflow Orchestration', 'Process Discovery', 'Automation Knowledge Base'],
    icon: ({ className }: { className?: string }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  {
    id: 'm365_copilot',
    name: 'M365 Copilot',
    description:
      'Open Microsoft 365 Copilot in your browser (Microsoft 365 work or school account required on the Microsoft side)',
    systemPrompt:
      'This path only opens the Microsoft 365 Copilot web app; it does not send your message to Microsoft from this assistant.',
    capabilities: ['Open Microsoft 365 Copilot', 'External web app'],
    icon: ({ className }: { className?: string }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
        />
      </svg>
    ),
  },
  {
    id: 'saleswise',
    name: 'SalesWise Agent',
    description:
      'Sales enablement partner for ConnectWise sellers — instant answers on deal rules, product positioning, competitive battlecards, Gong/Outreach/Clari guides, internal processes, and brand terminology, grounded in SharePoint.',
    systemPrompt:
      'You are the CW SalesWise Assistant. Answer only from retrieved SharePoint enablement content. Cite sources on every response. Do not guess or use outside knowledge.',
    capabilities: [
      'Product Knowledge',
      'Competitive Battlecards',
      'Sales Tool Guides',
      'Internal Processes',
      'Email Draft Support',
    ],
    icon: ({ className }: { className?: string }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
        />
      </svg>
    ),
  },
  {
    id: 'docs',
    name: 'ConnectWise University',
    description: 'Search ConnectWise University documentation and knowledge base',
    systemPrompt: 'You are a ConnectWise University assistant, helping users find documentation, training materials, and knowledge base articles.',
    capabilities: ['Documentation Search', 'Training Materials', 'Knowledge Base', 'Product Guides'],
    icon: ({ className }: { className?: string }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    )
  },
  {
    id: 'research',
    name: 'Web Research',
    description: 'Search the internet and read web pages to research any topic in depth',
    systemPrompt: 'You are a web research assistant that searches the internet and reads pages to answer questions thoroughly.',
    capabilities: ['Web Search', 'Read Web Pages', 'Multi-Step Research', 'Source Citations'],
    icon: ({ className }: { className?: string }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
      </svg>
    )
  },
  {
    id: 'perplexity_research',
    name: 'Perplexity Research',
    description: 'Search the web with Perplexity Search API and synthesize cited answers',
    systemPrompt:
      'You are a research assistant that uses Perplexity Search results as primary web evidence. Answer thoroughly with clear source citations.',
    capabilities: ['Perplexity Search', 'Ranked Web Results', 'Source Citations', 'Current Topics'],
    icon: ({ className }: { className?: string }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    )
  },
  {
    id: 'prospecting',
    name: 'Prospecting',
    description: 'Search ZoomInfo with your own seller licence. Connect ZoomInfo once, then search by company name.',
    systemPrompt:
      'You are the Prospecting agent. Search ZoomInfo with the signed-in rep\'s own ZoomInfo account. Do not use a shared ZoomInfo login.',
    capabilities: ['ZoomInfo SSO', 'Company search', 'Rep credit limit'],
    icon: ({ className }: { className?: string }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
      </svg>
    )
  }
];

const normalizeFavoriteAgents = (
  agentIds: string[],
  allowed: Set<string> | null = null,
) => {
  const baseIds = AVAILABLE_AGENTS.map(agent => agent.id);
  const pool = allowed && allowed.size > 0 ? baseIds.filter((id) => allowed.has(id)) : baseIds;
  const availableAgentIds = new Set(pool);
  return Array.from(new Set(agentIds)).filter(agentId => availableAgentIds.has(agentId)).slice(0, 6);
};


// Helper – shorten a source document path to a readable button label
const shortenAihsSourceDocument = (link: string | { urls?: string | string[] }, agentId?: string): string => {
  if (agentId === 'hyperautomation') {
    return extarctHyperautomationLabel(link as string) ?? (link as string);
  }
  if (typeof link === 'object' && link !== null && 'urls' in link) {
    const url = Array.isArray(link.urls) ? link.urls[0] : link.urls;
    if (typeof url === 'string') return url;
  }
  const linkStr = typeof link === 'string' ? link : '';
  const filename = linkStr.split('/').pop() || linkStr;
  const name = filename.replace(/\.pdf$/i, '');
  return name.length > 40 ? name.slice(0, 37) + '...' : name;
};

// Helper – deduplicate (link, page) pairs while preserving order
const deduplicateLinkPages = (
  links: string[],
  pages: number[]
): Array<{ link: string; page: number }> => {
  const seen = new Set<string>();
  const result: Array<{ link: string; page: number }> = [];
  links.forEach((link, i) => {
    const key = `${link}::${pages[i] ?? 1}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push({ link, page: pages[i] ?? 1 });
    }
  });
  return result;
};

const MAX_REFERENCE_TITLE_LEN = 80;

/** Make a reference title safe for a button label: no raw JSON/escapes/markdown, bounded length. */
const sanitizeReferenceTitle = (title: string, url: string): string => {
  let t = title
    .replace(/\\r\\n|\\n|\\r|\\t/g, ' ')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/^\s*\{\s*"text"\s*:\s*"/, '')
    .replace(/[#*_`]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!t || t.startsWith('{') || t.startsWith('[')) {
    return shortenAihsSourceDocument(url);
  }
  if (t.length > MAX_REFERENCE_TITLE_LEN) {
    t = `${t.slice(0, MAX_REFERENCE_TITLE_LEN - 1).trimEnd()}…`;
  }
  return t;
};

/** Parse PSA-KB / backend reference links: plain URLs, dicts, or stringified dicts. */
const parseRagTitleUrlLink = (raw: unknown): RagTitleUrlLink | null => {
  if (typeof raw === 'object' && raw !== null) {
    const obj = raw as Record<string, unknown>;
    const title = String(obj.title ?? '').trim();
    const url = String(obj.urls ?? obj.url ?? '').trim();
    if (!title || !url) return null;
    return { title: sanitizeReferenceTitle(title, url), url };
  }
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) {
    return { title: shortenAihsSourceDocument(trimmed), url: trimmed };
  }

  const titleMatch = trimmed.match(/'title'\s*:\s*'((?:\\'|[^'])*)'/);
  const urlMatch =
    trimmed.match(/'urls'\s*:\s*'((?:\\'|[^'])*)'/) ||
    trimmed.match(/'url'\s*:\s*'((?:\\'|[^'])*)'/);

  const title = titleMatch?.[1]?.replace(/\\'/g, "'").trim();
  const url = urlMatch?.[1]?.replace(/\\'/g, "'").trim() ?? '';

  if (!title || !url) return null;
  return { title: sanitizeReferenceTitle(title, url), url };
};

const deduplicateTitleUrlLinks = (links: ReferenceLinkPayload[]): RagTitleUrlLink[] => {
  const seen = new Set<string>();
  const result: RagTitleUrlLink[] = [];
  for (const raw of links) {
    const parsed = parseRagTitleUrlLink(raw);
    if (!parsed) continue;
    const key = `${parsed.title}::${parsed.url}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(parsed);
  }
  return result;
};

async function copyTextToClipboard(text: string): Promise<boolean> {
  const value = text.trim();
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fallback below */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// Message component
const MessageComponent: React.FC<{
  message: Message;
  onFeedback: (messageId: string, feedback: 'positive' | 'negative') => void;
  onRetry: (messageId: string) => void;
  onPlanChange: (messageId: string, planType: 'simple' | 'complex') => void;
  onAihsSourceDocument: (link: string, page: number) => void;
  isStreaming?: boolean;
  isRegenerating?: boolean;
  theme: 'dark' | 'light';
  showTimestamps: boolean;
  isFullscreen?: boolean;
  sidebarCollapsed?: boolean;
}> = ({ message, onFeedback, onRetry, onPlanChange, onAihsSourceDocument, isStreaming, isRegenerating, theme, showTimestamps, isFullscreen, sidebarCollapsed }) => {
  const isUser = message.role === 'user';
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const handleCopyResponse = useCallback(async () => {
    const ok = await copyTextToClipboard(message.content);
    setCopyState(ok ? 'copied' : 'failed');
    window.setTimeout(() => setCopyState('idle'), 2000);
  }, [message.content]);

  const copyButtonTitle =
    copyState === 'copied' ? 'Copied!' : copyState === 'failed' ? 'Copy failed' : 'Copy response';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} space-x-3`}>
      {!isUser && (() => {
        const messageAgent = message.agent ? AVAILABLE_AGENTS.find(a => a.id === message.agent) : null;
        return messageAgent ? (
          <div className={`w-8 h-8 rounded-full ${getAgentGradient(messageAgent.id)} flex items-center justify-center flex-shrink-0`}>
            <messageAgent.icon className="w-4 h-4 text-white" />
          </div>
        ) : (
          <div className={`w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'} flex items-center justify-center flex-shrink-0`}>
            <AIAssistantLogo size={20} />
          </div>
        );
      })()}

      <div
        className={`${(isFullscreen || sidebarCollapsed) ? '' : 'max-w-2xl'} ${isUser ? 'ml-12' : 'mr-12'}`}
        style={(isFullscreen || sidebarCollapsed) ? {
          maxWidth: isFullscreen ? '2048px' : sidebarCollapsed ? '1200px' : '768px'
        } : {}}
      >
        <div className={`rounded-lg p-4 ${isUser
          ? theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
          : theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white border border-gray-200 shadow-sm'
          }`}>
          <div className={`prose prose-sm max-w-none ${!isUser && theme === 'light' ? 'prose-slate' : ''}`} style={!isUser && theme === 'light' ? { color: '#111827' } : {}}>
            {isUser ? (
              // User messages: render as plain text with pre-wrap for line breaks
              <pre className="whitespace-pre-wrap font-sans text-sm text-white">{message.content}</pre>
            ) : (
              <MarkdownRenderer
                content={message.content}
                theme={theme}
                onPdfLink={(href) => {
                  if (message.agent === 'saleswise') {
                    // SalesWise PDFs are hosted on SharePoint — open externally, not in the in-app viewer.
                    void openExternalLink(href);
                    return;
                  }
                  onAihsSourceDocument(href, 1);
                }}
              />
            )}
            {isStreaming && (
              <span className="inline-block w-2 h-4 bg-cyan-400 animate-pulse ml-1"></span>
            )}
          </div>
          {message.excelBase64 && !isUser && !isStreaming && (
            <button
              type="button"
              onClick={() => downloadExcelAttachment(
                message.excelBase64 || '',
                message.excelFilename || 'zoominfo-contacts.xlsx',
              )}
              className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-cyan-400 hover:bg-gray-600 hover:border-cyan-500'
                : 'bg-gray-100 border-gray-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400'
                }`}
            >
              Download Excel
            </button>
          )}
        </div>

        {/* Interactive Steps Component */}
        {message.interactive_steps && !isUser && !isStreaming && (
          <InteractiveSteps
            title={message.interactive_steps.title}
            steps={message.interactive_steps.steps.map(step => ({
              ...step,
              completed: false
            }))}
            onStepsUpdate={(updatedSteps) => {
              // Handle steps update if needed
              console.log('Steps updated:', updatedSteps);
            }}
            onExecute={(selectedSteps) => {
              // Handle execute action if needed
              console.log('Execute selected steps:', selectedSteps);
            }}
            onPlanChange={(planType) => {
              onPlanChange(message.id, planType);
            }}
            isRegenerating={isRegenerating}
          />
        )}


        {/* AIHS use-case reference links (agent-specific rendering) */}
        {!isUser && !isStreaming && message.agent && aihs_use_cases.includes(message.agent) &&
          message.links && message.links.length > 0 && (
            message.agent === 'psa_kb' ? (() => {
              const articles = deduplicateTitleUrlLinks(message.links);
              if (articles.length === 0) return null;
              const linkButtonClass = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap ${theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-cyan-400 hover:bg-gray-600 hover:border-cyan-500'
                : 'bg-gray-100 border-gray-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400'
                }`;
              return (
                <div className="mt-3">
                  <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Related articles:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {articles.map(({ title, url }, idx) => (
                      <button
                        key={idx}
                        type="button"
                        data-open-in-app="true"
                        onClick={() => onAihsSourceDocument(url, 1)}
                        title={url}
                        className={linkButtonClass}
                      >
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        <span className="max-w-[18rem] truncate">{title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })() : (() => {
              const pdfLinks = message.links.filter((l): l is string => typeof l === 'string');
              const uniquePairs = deduplicateLinkPages(pdfLinks, message.pages || []);
              if (uniquePairs.length === 0) return null;
              return (
                <div className="mt-3">
                  <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Source documents:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {uniquePairs.map(({ link, page }, idx) => (
                      <button
                        key={idx}
                        type="button"
                        data-open-in-app="true"
                        onClick={() => onAihsSourceDocument(link, page)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-cyan-400 hover:bg-gray-600 hover:border-cyan-500'
                          : 'bg-gray-100 border-gray-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400'
                          }`}
                      >
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>{shortenAihsSourceDocument(link, message.agent)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()
          )}

        {/* SalesWise source links — all open externally in SharePoint (browser/Teams), never in the in-app viewer */}
        {!isUser && !isStreaming && message.agent === 'saleswise' &&
          message.links && message.links.length > 0 && (() => {
            const linkButtonClass = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-cyan-400 hover:bg-gray-600 hover:border-cyan-500'
              : 'bg-gray-100 border-gray-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400'
              }`;
            const externalLinkIcon = (
              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            );

            // Try dict-format first (title+url), then fall back to plain-URL strings
            const dictLinks = deduplicateTitleUrlLinks(message.links);
            if (dictLinks.length > 0) {
              return (
                <div className="mt-3">
                  <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Sources:</p>
                  <div className="flex flex-wrap gap-2">
                    {dictLinks.map(({ title, url }, idx) => (
                      <button
                        key={idx}
                        type="button"
                        data-open-in-app="true"
                        onClick={() => void openExternalLink(url)}
                        title={url}
                        className={linkButtonClass}
                      >
                        {externalLinkIcon}
                        <span className="max-w-[18rem] truncate">{title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            }
            // Fallback: plain string URLs
            const stringLinks = message.links.filter((l): l is string => typeof l === 'string');
            const uniquePairs = deduplicateLinkPages(stringLinks, message.pages || []);
            if (uniquePairs.length === 0) return null;
            return (
              <div className="mt-3">
                <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Sources:</p>
                <div className="flex flex-wrap gap-2">
                  {uniquePairs.map(({ link }, idx) => (
                    <button
                      key={idx}
                      type="button"
                      data-open-in-app="true"
                      onClick={() => void openExternalLink(link)}
                      title={link}
                      className={linkButtonClass}
                    >
                      {externalLinkIcon}
                      <span>{shortenAihsSourceDocument(link, message.agent)}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

        {/* University / Web Research reference links (PSA-KB-style buttons when backend sends links) */}
        {!isUser && !isStreaming && message.links && message.links.length > 0 &&
          (message.agent === 'docs' || message.agent === 'research') && (() => {
            const articles = deduplicateTitleUrlLinks(message.links);
            if (articles.length === 0) return null;
            const linkButtonClass = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-cyan-400 hover:bg-gray-600 hover:border-cyan-500'
              : 'bg-gray-100 border-gray-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400'
              }`;
            const heading = message.agent === 'docs' ? 'References:' : 'Sources:';
            return (
              <div className="mt-3">
                <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {heading}
                </p>
                <div className="flex flex-wrap gap-2">
                  {articles.map(({ title, url }, idx) => (
                    <button
                      key={idx}
                      type="button"
                      data-open-in-app="true"
                      onClick={() => onAihsSourceDocument(url, 1)}
                      title={url}
                      className={linkButtonClass}
                    >
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span className="max-w-[18rem] truncate">{title}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

        {!isUser && !isStreaming && (
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => void handleCopyResponse()}
                className={`p-1.5 rounded transition-colors ${copyState === 'copied'
                  ? 'text-green-400 bg-green-400/10'
                  : copyState === 'failed'
                    ? 'text-red-400 bg-red-400/10'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'
                  }`}
                title={copyButtonTitle}
                aria-label={copyButtonTitle}
              >
                {copyState === 'copied' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </button>

              <button
                onClick={() => onFeedback(message.id, 'positive')}
                className={`p-1.5 rounded transition-colors ${message.feedback === 'positive'
                  ? 'text-green-400 bg-green-400/10'
                  : 'text-gray-400 hover:text-green-400 hover:bg-green-400/10'
                  }`}
                title="Good response"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
              </button>

              <button
                onClick={() => onFeedback(message.id, 'negative')}
                className={`p-1.5 rounded transition-colors ${message.feedback === 'negative'
                  ? 'text-red-400 bg-red-400/10'
                  : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                  }`}
                title="Poor response"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.106-1.79l-.05-.025A4 4 0 0011.157 2H5.741a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.439 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                </svg>
              </button>

              <button
                onClick={() => onRetry(message.id)}
                className="p-1.5 rounded transition-colors text-gray-400 hover:text-white hover:bg-gray-700"
                title="Retry"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>

            <span className="text-xs text-gray-400">
              {showTimestamps && message.timestamp.toLocaleTimeString()}
              {showTimestamps && message.agent && ` • `}
              {message.agent && `${AVAILABLE_AGENTS.find(a => a.id === message.agent)?.name || message.agent}`}
            </span>
          </div>
        )}

        {isUser && showTimestamps && (
          <div className="flex justify-end mt-2">
            <span className="text-xs text-gray-400">
              {message.timestamp.toLocaleTimeString()}
            </span>
          </div>
        )}
      </div>

      {isUser && (
        <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </div>
  );
};

// Main ChatBot component
const ChatBot: React.FC = () => {
  // State
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string>('');
  const [inputValue, setInputValue] = useState('');
  const [loadingSessions, setLoadingSessions] = useState<Set<string>>(new Set());
  const [unreadSessions, setUnreadSessions] = useState<Set<string>>(new Set());
  const [streamingStatus, setStreamingStatus] = useState<string>('');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [supportInfoOpen, setSupportInfoOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showPromptGallery, setShowPromptGallery] = useState(false);
  const [pdfDocument, setPdfDocument] = useState('');
  const [initialPdfPage, setInitialPage] = useState(1);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);

  // Mobile detection
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      window.innerWidth <= 768;
  };

  // Teams detection (early, used for default state)
  const isInTeams = isTeamsHost();

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [editingChatId, setEditingChatId] = useState<string>('');
  const [editingTitle, setEditingTitle] = useState('');
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [smartSuggestions, setSmartSuggestions] = useState<{
    greeting: string;
    suggestions: (string | { text: string; agent?: string | null })[];
    personalized: boolean;
    reason: string;
  } | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  // Favorite agents functionality
  const [favoriteAgents, setFavoriteAgents] = useState<string[]>(() => {
    const saved = localStorage.getItem('sidekick-favorite-agents');
    try {
      const parsed = saved ? JSON.parse(saved) : ['gpt', 'docs', 'noc_kb'];
      const normalized = normalizeFavoriteAgents(Array.isArray(parsed) ? parsed : ['gpt', 'docs', 'noc_kb']);
      localStorage.setItem('sidekick-favorite-agents', JSON.stringify(normalized));
      return normalized;
    } catch {
      return ['gpt', 'docs', 'noc_kb']; // Default favorites
    }
  });
  const [showAllAgents, setShowAllAgents] = useState(false);

  // Save settings to localStorage
  const saveSettings = (newSettings: Settings) => {
    try {
      localStorage.setItem('sidekick-settings', JSON.stringify(newSettings));
      setSettings(newSettings);
      // Update sidebar state if that setting changed
      if (newSettings.startWithCollapsedSidebar !== settings.startWithCollapsedSidebar) {
        setSidebarCollapsed(newSettings.startWithCollapsedSidebar);
      }
      if (newSettings.startInFullscreenMode !== settings.startInFullscreenMode && !isMobile()) {
        setIsFullscreen(newSettings.startInFullscreenMode);
      }
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }
  };

  // Reset settings to defaults
  const resetSettings = () => {
    const defaults = getDefaultSettings();
    saveSettings(defaults);
  };

  // Load settings from localStorage or use defaults
  const loadSettings = (): Settings => {
    try {
      const saved = localStorage.getItem('sidekick-settings');
      if (saved) {
        const parsed = JSON.parse(saved) as Record<string, unknown>;
        delete parsed.model;
        const merged = { ...getDefaultSettings(), ...parsed } as Settings;

        // One-time migration: force fullscreen mode on for users who already had
        // settings saved before fullscreen became the default, while still letting
        // them opt back out afterward.
        const FULLSCREEN_MIGRATION_KEY = 'sidekick-fullscreen-default-migrated';
        if (!localStorage.getItem(FULLSCREEN_MIGRATION_KEY)) {
          merged.startInFullscreenMode = true;
          localStorage.setItem('sidekick-settings', JSON.stringify(merged));
          localStorage.setItem(FULLSCREEN_MIGRATION_KEY, 'true');
        }

        return merged;
      }
    } catch (error) {
      console.warn('Failed to load settings from localStorage:', error);
    }
    return getDefaultSettings();
  };

  const getDefaultSettings = (): Settings => ({
    theme: 'dark',
    fontSize: 'medium',
    notifications: true,
    autoSave: true,
    streamingEnabled: true,
    temperature: 0.7,
    showMessageTimestamps: true,
    startWithCollapsedSidebar: false,
    startInFullscreenMode: true,
    chatRetentionDays: 30
  });

  const [settings, setSettings] = useState<Settings>(loadSettings);
  const [isFullscreen, setIsFullscreen] = useState(
    () => isMobile() || loadSettings().startInFullscreenMode
  );
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirmDialog(settings.theme);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    isMobile() || isInTeams ? true : loadSettings().startWithCollapsedSidebar
  );

  // Azure Speech-to-text — disabled (see useSpeechRecognition.ts stub; SDK + Vite issue).
  // const handleSpeechResult = React.useCallback((text: string) => {
  //   setInputValue((prev) => (prev ? prev + ' ' + text : text));
  // }, []);
  // const {
  //   isListening,
  //   interimText,
  //   error: speechError,
  //   toggleListening,
  //   stopListening,
  // } = useSpeechRecognition(handleSpeechResult);
  // Azure Speech UI is currently disabled (see commented markup below).

  const themeClasses = {
    bg: {
      primary: settings.theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50',
      secondary: settings.theme === 'dark' ? 'bg-gray-800' : 'bg-white',
      tertiary: settings.theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100',
      hover: settings.theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100',
      input: settings.theme === 'dark' ? 'bg-gray-700' : 'bg-white',
    },
    text: {
      primary: settings.theme === 'dark' ? 'text-white' : 'text-gray-900',
      secondary: settings.theme === 'dark' ? 'text-gray-300' : 'text-gray-700',
      tertiary: settings.theme === 'dark' ? 'text-gray-400' : 'text-gray-500',
      muted: settings.theme === 'dark' ? 'text-gray-500' : 'text-gray-400',
    },
    border: {
      primary: settings.theme === 'dark' ? 'border-gray-700' : 'border-gray-200',
      secondary: settings.theme === 'dark' ? 'border-gray-600' : 'border-gray-300',
    },
    message: {
      user: settings.theme === 'dark' ? 'bg-blue-600' : 'bg-blue-500',
      assistant: settings.theme === 'dark' ? 'bg-gray-700' : 'bg-white border border-gray-200',
    }
  };

  // Fullscreen functionality (expands the chat div to fill viewport)
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Add keyboard shortcut for chat fullscreen (Ctrl+F)
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLowerCase() === 'f') {
        // Only if not in a text input
        const target = event.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          event.preventDefault();
          toggleFullscreen();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Track viewport size so layout can react to low-resolution windows (~1280x800 and below),
  // not just the mobile user-agent/width breakpoint.
  // Use AND so a large display with a short browser chrome height is still treated as normal.
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const isCompactViewport = windowSize.width <= 1280 && windowSize.height <= 800;

  // Handle window resize for mobile responsiveness
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = isMobile();

      setWindowSize({ width: window.innerWidth, height: window.innerHeight });

      // Auto-enable fullscreen on mobile, auto-disable on desktop
      if (mobile && !isFullscreen) {
        setIsFullscreen(true);
      }

      // Auto-collapse sidebar on mobile, respect settings on desktop
      if (mobile && !sidebarCollapsed) {
        setSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isFullscreen, sidebarCollapsed]);

  // Refs
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const titleEditRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const currentChatIdRef = useRef<string>(currentChatId);
  currentChatIdRef.current = currentChatId;

  // Derived: is the current session loading?
  const isLoading = loadingSessions.has(currentChatId);

  // Authentication (react-oidc-context)
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const [zoomInfoConnected, setZoomInfoConnected] = useState(false);
  const [zoomInfoError, setZoomInfoError] = useState('');
  const zoomInfoConnectedRef = useRef(false);
  const zoomInfoStatusReadyRef = useRef(false);

  const refreshZoomInfoStatus = useCallback(async () => {
    const token = authRef.current.user?.access_token;
    if (!token) {
      zoomInfoConnectedRef.current = false;
      zoomInfoStatusReadyRef.current = false;
      setZoomInfoConnected(false);
      return false;
    }
    try {
      const response = await fetch(getApiUrl('/zoominfo/status'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: { connected?: boolean } = response.ok ? await response.json() : { connected: false };
      const connected = data.connected === true;
      zoomInfoConnectedRef.current = connected;
      zoomInfoStatusReadyRef.current = true;
      setZoomInfoConnected(connected);
      return connected;
    } catch {
      zoomInfoConnectedRef.current = false;
      zoomInfoStatusReadyRef.current = true;
      setZoomInfoConnected(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!auth.user?.access_token) return;
    void refreshZoomInfoStatus();
  }, [auth.user?.access_token, refreshZoomInfoStatus]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'zoominfo-connected') {
        zoomInfoConnectedRef.current = true;
        zoomInfoStatusReadyRef.current = true;
        setZoomInfoConnected(true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const connectZoomInfo = (existingPopup?: Window | null) => {
    const token = auth.user?.access_token;
    if (!token) {
      existingPopup?.close();
      setZoomInfoError('Sign in to Colleague Assistant first. ZoomInfo uses that same sign-in.');
      return;
    }
    setZoomInfoError('');
    const redirectUri = `${window.location.origin}/zoominfo/callback`;
    void fetch(getApiUrl('/zoominfo/connect'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ redirect_uri: redirectUri }),
    })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          existingPopup?.close();
          const detail = typeof data.detail === 'string' ? data.detail : 'Could not start ZoomInfo sign-in.';
          setZoomInfoError(detail);
          return;
        }
        if (typeof data.authorize_url !== 'string') {
          existingPopup?.close();
          setZoomInfoError('ZoomInfo did not return a sign-in window.');
          return;
        }
        if (existingPopup && !existingPopup.closed) {
          existingPopup.location.href = data.authorize_url;
          return;
        }
        const popup = window.open(data.authorize_url, 'zoominfo-sso', 'popup,width=520,height=720');
        if (!popup) {
          setZoomInfoError('The browser blocked the ZoomInfo window. Allow pop-ups for this site, then select Prospecting again.');
        }
      })
      .catch(() => {
        existingPopup?.close();
        setZoomInfoError('Could not start ZoomInfo sign-in.');
      });
  };

  const chooseAgent = (agent: Agent | null) => {
    setSelectedAgent(agent);
    if (agent?.id !== 'prospecting') return;
    if (zoomInfoStatusReadyRef.current && zoomInfoConnectedRef.current) return;

    // Open during the click so the browser allows it, then close immediately when already connected.
    const popup = window.open('about:blank', 'zoominfo-sso', 'popup,width=520,height=720');
    if (!popup) {
      setZoomInfoError('The browser blocked the ZoomInfo window. Allow pop-ups for this site, then select Prospecting again.');
      return;
    }
    void (async () => {
      const connected = zoomInfoStatusReadyRef.current
        ? zoomInfoConnectedRef.current
        : await refreshZoomInfoStatus();
      if (connected) {
        popup.close();
        return;
      }
      connectZoomInfo(popup);
    })();
  };

  useEffect(() => {
    if (auth.isAuthenticated) {
      clearTeamsAuthInProgress();
    }
  }, [auth.isAuthenticated]);

  /** null = full catalog (RBAC off or not loaded); non-null Set = enforced allowlist (may be empty). */
  const [allowedAgentIds, setAllowedAgentIds] = useState<Set<string> | null>(null);

  const agentsForPicker = React.useMemo(() => {
    if (!allowedAgentIds) return AVAILABLE_AGENTS;
    return AVAILABLE_AGENTS.filter(a => allowedAgentIds.has(a.id));
  }, [allowedAgentIds]);

  const resolvePickerAgent = React.useCallback(
    (agentId: string | null | undefined) => {
      if (agentId) {
        const hit = agentsForPicker.find(a => a.id === agentId);
        if (hit) return hit;
      }
      return agentsForPicker.find(a => a.id === 'gpt') || agentsForPicker[0] || null;
    },
    [agentsForPicker],
  );

  const purgeServerMemorySession = React.useCallback(
    async (chatSessionId: string) => {
      const token = auth.user?.access_token;
      if (!token || !chatSessionId) return;
      try {
        await fetch(getApiUrl('/memory/purge-session'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ chat_session_id: chatSessionId }),
        });
      } catch (e) {
        console.warn('purgeServerMemorySession failed', e);
      }
    },
    [auth.user?.access_token],
  );

  const purgeSessionRef = useRef(purgeServerMemorySession);
  purgeSessionRef.current = purgeServerMemorySession;

  const runRetentionSweep = React.useCallback(() => {
    const limitMs = settings.chatRetentionDays * 86400000;
    if (limitMs <= 0) return;

    setChatSessions(prev => {
      if (prev.length === 0) return prev;
      const now = Date.now();
      const sorted = [...prev].sort(
        (a, b) => new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime(),
      );
      const mru = sorted[0];
      const removedIds: string[] = [];
      const kept: ChatSession[] = [];

      for (const s of prev) {
        const age = now - new Date(s.lastActive).getTime();
        const tooOld = age > limitMs;
        if (tooOld && mru && s.id !== mru.id) {
          removedIds.push(s.id);
          continue;
        }
        kept.push(s);
      }

      if (removedIds.length === 0) return prev;

      queueMicrotask(() => {
        removedIds.forEach(id => {
          void purgeSessionRef.current(id);
        });
      });

      let next = kept;
      if (next.length === 0) {
        next = [
          {
            id: generateId(),
            title: 'New Chat',
            messages: [],
            lastActive: new Date(),
          },
        ];
      }

      const curId = currentChatIdRef.current;
      if (!next.some(c => c.id === curId)) {
        setCurrentChatId(next[0].id);
      }

      return next;
    });
  }, [settings.chatRetentionDays]);

  const runRetentionRef = useRef(runRetentionSweep);
  runRetentionRef.current = runRetentionSweep;

  useEffect(() => {
    const tok = auth.user?.access_token;
    if (!tok) {
      setAllowedAgentIds(null);
      return;
    }
    let cancelled = false;
    let fetchGen = 0;

    const loadAllowedAgents = () => {
      const t = authRef.current.user?.access_token;
      if (!t) {
        setAllowedAgentIds(null);
        return;
      }
      const myGen = ++fetchGen;
      fetch(getApiUrl('/me/agents'), { headers: { Authorization: `Bearer ${t}` } })
        .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((data: { agents?: string[]; rbac_enforced?: boolean }) => {
          if (cancelled || myGen !== fetchGen) return;
          const ids = Array.isArray(data.agents) ? data.agents : [];
          // Backend may return a subset even when rbac_enforced is false (e.g. Dynamo default grants without Perplexity).
          if (ids.length > 0) {
            setAllowedAgentIds(new Set(ids));
            return;
          }
          if (data.rbac_enforced) {
            setAllowedAgentIds(new Set());
            return;
          }
          setAllowedAgentIds(null);
        })
        .catch(() => {
          if (!cancelled && myGen === fetchGen) setAllowedAgentIds(null);
        });
    };

    loadAllowedAgents();

    const onRefreshGrants = () => {
      if (document.visibilityState !== 'visible') return;
      loadAllowedAgents();
    };
    window.addEventListener('focus', onRefreshGrants);
    document.addEventListener('visibilitychange', onRefreshGrants);

    return () => {
      cancelled = true;
      fetchGen += 1;
      window.removeEventListener('focus', onRefreshGrants);
      document.removeEventListener('visibilitychange', onRefreshGrants);
    };
  }, [auth.user?.access_token]);

  useEffect(() => {
    if (allowedAgentIds === null) return;
    const allow = allowedAgentIds;
    setFavoriteAgents(prev => {
      const next = normalizeFavoriteAgents(prev, allow);
      if (JSON.stringify(next) !== JSON.stringify(prev)) {
        localStorage.setItem('sidekick-favorite-agents', JSON.stringify(next));
      }
      return next;
    });
    setSelectedAgent(cur => {
      if (agentsForPicker.length === 0) return null;
      if (!cur || !allow.has(cur.id)) return agentsForPicker[0] ?? null;
      return cur;
    });
  }, [allowedAgentIds, agentsForPicker]);

  useEffect(() => {
    if (agentsForPicker.length === 0) return;
    setSelectedAgent(cur => {
      if (cur) return cur;
      let savedId: string | null = null;
      try {
        savedId = localStorage.getItem(SELECTED_AGENT_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      return (
        (savedId ? agentsForPicker.find(a => a.id === savedId) : undefined) ||
        agentsForPicker.find(a => a.id === 'gpt') ||
        agentsForPicker[0] ||
        null
      );
    });
  }, [agentsForPicker]);

  // Persist the user's agent choice so reloads/remounts (Teams iframe, token renewal) don't reset to GPT.
  useEffect(() => {
    if (!selectedAgent) return;
    try {
      localStorage.setItem(SELECTED_AGENT_STORAGE_KEY, selectedAgent.id);
    } catch {
      /* ignore */
    }
  }, [selectedAgent]);

  useEffect(() => {
    runRetentionSweep();
    const id = window.setInterval(runRetentionSweep, 60 * 60 * 1000);
    return () => clearInterval(id);
  }, [runRetentionSweep]);

  // Auto-save chat sessions to localStorage
  useEffect(() => {
    if (chatSessions.length > 0) {
      try {
        localStorage.setItem('sidekick-chat-sessions', JSON.stringify(chatSessions));
      } catch (error) {
        console.error('Failed to auto-save chat sessions:', error);
      }
    }
  }, [chatSessions]);

  // Load chat sessions from localStorage on startup
  useEffect(() => {
    try {
      const savedSessions = localStorage.getItem('sidekick-chat-sessions');
      if (savedSessions) {
        const parsed = JSON.parse(savedSessions);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Parse dates back to Date objects
          const sessionsWithDates = parsed.map(session => ({
            ...session,
            lastActive: new Date(session.lastActive),
            messages: session.messages.map((msg: any) => ({
              ...msg,
              timestamp: new Date(msg.timestamp)
            }))
          }));
          setChatSessions(sessionsWithDates);
          setCurrentChatId(sessionsWithDates[0].id);
          window.setTimeout(() => runRetentionRef.current(), 0);
          return; // Skip creating default chat if we loaded sessions
        }
      }
    } catch (error) {
      console.error('Failed to load saved chat sessions:', error);
    }

    // Only create default chat if no sessions were loaded
    if (chatSessions.length === 0) {
      const firstChat: ChatSession = {
        id: generateId(),
        title: 'New Chat',
        messages: [],
        lastActive: new Date()
      };
      setChatSessions([firstChat]);
      setCurrentChatId(firstChat.id);
    }
  }, []); // Run once on mount



  // Update sidebar collapse state when settings change
  useEffect(() => {
    setSidebarCollapsed(settings.startWithCollapsedSidebar);
  }, [settings.startWithCollapsedSidebar]);

  // Apply fullscreen default when setting changes (desktop only; mobile always fullscreen)
  useEffect(() => {
    if (!isMobile()) {
      setIsFullscreen(settings.startInFullscreenMode);
    }
  }, [settings.startInFullscreenMode]);

  // Auto-focus input
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [currentChatId]);

  // Scroll to bottom of messages (messages pane only — never the window)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [chatSessions, currentChatId]);

  // Auto-scroll when messages change (for streaming) - but only if near bottom
  useEffect(() => {
    const currentChat = chatSessions.find(chat => chat.id === currentChatId);
    const currentMessages = currentChat?.messages || [];

    if (currentMessages.length > 0) {
      // Don't force scroll - only scroll if user is already near bottom
      scrollToBottom(false);
    }
  }, [
    chatSessions.find(chat => chat.id === currentChatId)?.messages?.map(m => m.content).join('').length,
    currentChatId
  ]);

  // Focus title edit input
  useEffect(() => {
    if (editingChatId && titleEditRef.current) {
      titleEditRef.current.focus();
      titleEditRef.current.select();
    }
  }, [editingChatId]);

  // Helper functions
  const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

  const scrollToBottom = (force = false) => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;

      // Check if user is near the bottom (within 100px) or if we're forcing scroll
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      // Force scroll for new messages, or auto-scroll if user is at bottom
      if (force || (!isUserScrolling && isNearBottom)) {
        setTimeout(() => {
          const el = messagesContainerRef.current;
          if (el) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            // Reset user scrolling state when we auto-scroll
            if (!force) {
              setIsUserScrolling(false);
            }
          }
        }, 50);
      }
    }
  };

  // Add scroll event listener to detect user scroll intent
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const threshold = 100;
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
      setIsUserScrolling(!isAtBottom);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [currentChatId, chatSessions.length]); // Re-run when chat changes

  // Keyboard shortcuts for modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showAllAgents) {
          setShowAllAgents(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showAllAgents]);

  const getCurrentMessages = () => {
    const currentChat = chatSessions.find(chat => chat.id === currentChatId);
    return currentChat?.messages || [];
  };

  const updateCurrentChat = (updater: (chat: ChatSession) => ChatSession) => {
    setChatSessions(prev => prev.map(chat =>
      chat.id === currentChatId ? updater(chat) : chat
    ));
  };

  const updateChatById = (chatId: string, updater: (chat: ChatSession) => ChatSession) => {
    setChatSessions(prev => prev.map(chat =>
      chat.id === chatId ? updater(chat) : chat
    ));
  };

  const setSessionLoading = (chatId: string, loading: boolean) => {
    setLoadingSessions(prev => {
      const next = new Set(prev);
      if (loading) next.add(chatId);
      else next.delete(chatId);
      return next;
    });
  };

  const markSessionUnread = (chatId: string) => {
    setUnreadSessions(prev => {
      const next = new Set(prev);
      next.add(chatId);
      return next;
    });
  };

  const clearSessionUnread = (chatId: string) => {
    setUnreadSessions(prev => {
      const next = new Set(prev);
      next.delete(chatId);
      return next;
    });
  };

  // Usage tracking
  interface UsageEntry {
    date: string;
    model: string;
    input_tokens: number;
    output_tokens: number;
  }

  const recordUsage = (usage: { input_tokens?: number; output_tokens?: number; total_tokens?: number }, model?: string) => {
    if (!usage || (!usage.input_tokens && !usage.output_tokens)) return;
    const today = new Date().toISOString().slice(0, 10);
    const entry: UsageEntry = {
      date: today,
      model: model || 'unknown',
      input_tokens: usage.input_tokens || 0,
      output_tokens: usage.output_tokens || 0,
    };
    try {
      const raw = localStorage.getItem('sidekick-usage') || '[]';
      const entries: UsageEntry[] = JSON.parse(raw);
      entries.push(entry);
      // Keep only last 90 days
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 90);
      const cutoffStr = cutoff.toISOString().slice(0, 10);
      const trimmed = entries.filter(e => e.date >= cutoffStr);
      localStorage.setItem('sidekick-usage', JSON.stringify(trimmed));
    } catch { /* ignore storage errors */ }
  };

  const [clientChatConfig, setClientChatConfig] = useState<ClientChatConfig | null>(null);
  /** Separate from `clientChatConfig === null` so we distinguish "still fetching" / failed from "loaded and false". */
  const [clientChatConfigFetch, setClientChatConfigFetch] = useState<'loading' | 'ok' | 'error'>('loading');

  // Fetch server config (read-only main chat model label; spend_limit included for future use)
  useEffect(() => {
    const token = auth.user?.access_token?.trim();
    if (!token) {
      setClientChatConfig(null);
      setClientChatConfigFetch('loading');
      return;
    }

    let cancelled = false;
    setClientChatConfigFetch('loading');
    fetch(getApiUrl('/config'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<ClientChatConfig>;
      })
      .then((data: ClientChatConfig) => {
        if (cancelled) return;
        setClientChatConfig(data);
        setClientChatConfigFetch('ok');
      })
      .catch(() => {
        if (cancelled) return;
        setClientChatConfig(null);
        setClientChatConfigFetch('error');
      });
    return () => {
      cancelled = true;
    };
  }, [auth.user?.access_token]);

  useEffect(() => {
    if (selectedAgent) {
      window.clarity?.('set', 'agent', selectedAgent.id);
    }
  }, [selectedAgent]);

  // File handling functions
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
    if (event.target) event.target.value = '';
    if (files.length > 0) {
      window.clarity?.('event', 'file_attached');
      window.clarity?.('set', 'file_count', String(files.length));
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Generate smart suggestions based on chat history
  const generateSmartSuggestions = () => {
    const defaultSuggestions = [
      'How do I install the PSA client?',
      "Who are ConnectWise's competitors?"
    ];

    const allMessages = chatSessions.flatMap(session => session.messages);
    const userQueries = allMessages
      .filter(msg => msg.role === 'user')
      .map(msg => msg.content.trim())
      .filter(content => content.length > 0)
      .slice(-20);

    if (userQueries.length === 0) {
      return defaultSuggestions;
    }

    // Use recent unique queries as suggestions
    const uniqueQueries = [...new Set(userQueries)].slice(-3);

    // Fill remaining slots with defaults
    while (uniqueQueries.length < 2) {
      const defaultToAdd = defaultSuggestions.find(def => !uniqueQueries.includes(def));
      if (defaultToAdd) {
        uniqueQueries.push(defaultToAdd);
      } else {
        break;
      }
    }

    return uniqueQueries.slice(0, 3);
  };

  // Fetch AI-powered smart suggestions from backend
  const fetchSmartSuggestions = async () => {
    if (loadingSuggestions) return;

    const token = auth.user?.access_token?.trim();
    if (!token) return;

    setLoadingSuggestions(true);
    try {
      const response = await fetch(getApiUrl('/smart-suggestions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          chat_history: chatSessions.map(session => ({
            id: session.id,
            title: session.title,
            messages: session.messages.map(msg => ({
              role: msg.role,
              content: msg.content,
              timestamp: msg.timestamp,
              agent: msg.agent
            }))
          })),
          user_context: {
            current_chat_id: currentChatId,
            total_sessions: chatSessions.length
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSmartSuggestions(data);
      } else {
        console.error('Failed to fetch smart suggestions:', response.status);
        // Fallback to local generation
        setSmartSuggestions({
          greeting: "Hello! 👋\n\nI'm having trouble connecting to the AI suggestion service right now, but here are some things you can ask:",
          suggestions: generateSmartSuggestions(),
          personalized: false,
          reason: "Using local fallback due to API error"
        });
      }
    } catch (error) {
      console.error('Error fetching smart suggestions:', error);
      // Fallback to local generation
      setSmartSuggestions({
        greeting: "Hello! 👋\n\nWelcome back to the ConnectWise AI Assistant. Here are some commands to get you started:",
        suggestions: generateSmartSuggestions(),
        personalized: false,
        reason: "Using local fallback due to network error"
      });
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Fetch suggestions when component mounts or chat sessions change
  React.useEffect(() => {
    fetchSmartSuggestions();
  }, [chatSessions.length, auth.user?.access_token]); // Re-fetch when sessions or auth change

  const formatRelativeTime = (date: Date | string | undefined) => {
    if (!date) return 'Unknown';

    // Convert to Date object if it's a string
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Check if it's a valid Date object
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return 'Unknown';
    }

    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return dateObj.toLocaleDateString();
  };

  // Chat functions
  const createNewChat = () => {
    const newChat: ChatSession = {
      id: generateId(),
      title: 'New Chat',
      messages: [],
      lastActive: new Date()
    };
    setChatSessions(prev => [newChat, ...prev]);
    setCurrentChatId(newChat.id);
    setInputValue('');
    window.clarity?.('event', 'new_conversation');
  };

  const deleteChat = (chatId: string) => {
    window.clarity?.('event', 'chat_deleted');
    void purgeServerMemorySession(chatId);
    console.log('Deleting chat:', chatId);
    setChatSessions(prev => {
      console.log('Previous chats:', prev.length);
      const filtered = prev.filter(chat => chat.id !== chatId);
      console.log('Filtered chats:', filtered.length);

      if (chatId === currentChatId && filtered.length > 0) {
        setCurrentChatId(filtered[0].id);
        return filtered;
      } else if (chatId === currentChatId && filtered.length === 0) {
        // If no chats left, create a new one
        const newChat: ChatSession = {
          id: generateId(),
          title: 'New Chat',
          messages: [],
          lastActive: new Date()
        };
        setCurrentChatId(newChat.id);
        return [newChat];
      }
      return filtered;
    });
  };

  const clearAllChats = () => {
    console.log('Clearing all chats');
    setChatSessions(prev => {
      prev.forEach(s => {
        void purgeServerMemorySession(s.id);
      });
      localStorage.removeItem('sidekick-chat-sessions');
      const newChat: ChatSession = {
        id: generateId(),
        title: 'New Chat',
        messages: [],
        lastActive: new Date()
      };
      setCurrentChatId(newChat.id);
      console.log('All chats cleared, new chat created');
      return [newChat];
    });
  };

  const startEditingTitle = (chatId: string, currentTitle: string) => {
    setEditingChatId(chatId);
    setEditingTitle(currentTitle);
  };

  const saveTitle = () => {
    if (editingChatId && editingTitle.trim()) {
      setChatSessions(prev => prev.map(chat =>
        chat.id === editingChatId
          ? { ...chat, title: editingTitle.trim() }
          : chat
      ));
    }
    setEditingChatId('');
    setEditingTitle('');
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveTitle();
    } else if (e.key === 'Escape') {
      setEditingChatId('');
      setEditingTitle('');
    }
  };

  // Message functions
  // Function to regenerate plan with different complexity
  const handlePlanChange = async (messageId: string, planType: 'simple' | 'complex') => {
    const currentChat = chatSessions.find(chat => chat.id === currentChatId);
    if (!currentChat) return;

    const messageIndex = currentChat.messages.findIndex(msg => msg.id === messageId);
    if (messageIndex === -1) return;

    const originalMessage = currentChat.messages[messageIndex];
    if (!originalMessage.interactive_steps) return;

    // Find the previous user message to regenerate the response
    let userMessageIndex = messageIndex - 1;
    while (userMessageIndex >= 0 && currentChat.messages[userMessageIndex].role !== 'user') {
      userMessageIndex--;
    }

    if (userMessageIndex < 0) return;

    const userMessage = currentChat.messages[userMessageIndex];

    setSessionLoading(currentChatId, true);

    try {
      // Get SSO token from auth context
      const ssoToken = auth.user?.access_token || '';

      const formData = new FormData();
      formData.append('message', userMessage.content);
      formData.append('sso_token', ssoToken);
      formData.append('cw_user_auth_details', JSON.stringify(buildCwUserAuthDetails(auth.user)));
      formData.append('chat_session_id', currentChatId);
      formData.append('temperature', settings.temperature.toString());
      formData.append('plan_type', planType);
      formData.append('agent', selectedAgent?.id || 'gpt');
      formData.append('fileCount', '0');

      const apiUrl = getApiUrl('/chat');
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errReply = await chatErrorReplyFromResponse(response);
        updateCurrentChat(chat => ({
          ...chat,
          messages: chat.messages.map(msg =>
            msg.id === messageId
              ? {
                ...msg,
                content: errReply,
                interactive_steps: msg.interactive_steps,
              }
              : msg
          ),
          lastActive: new Date(),
        }));
        return;
      }

      const data = await response.json();

      // Update the existing message with new plan
      updateCurrentChat(chat => ({
        ...chat,
        messages: chat.messages.map(msg =>
          msg.id === messageId
            ? {
              ...msg,
              content: data.reply || 'Sorry, I encountered an error processing your request.',
              interactive_steps: data.interactive_steps || msg.interactive_steps,
              excelBase64: undefined,
              excelFilename: undefined,
              ...excelFieldsFromReply(data),
            }
            : msg
        ),
        lastActive: new Date()
      }));

    } catch (error) {
      console.error('Error regenerating plan:', error);
      // Show error to user - you might want to add a toast notification here
    } finally {
      setSessionLoading(currentChatId, false);
    }
  };

  // Favorite agents functionality
  const toggleFavoriteAgent = (agentId: string) => {
    setFavoriteAgents(prev => {
      const normalizedPrev = normalizeFavoriteAgents(prev, allowedAgentIds);
      const isRemoving = normalizedPrev.includes(agentId);
      let newFavorites;
      if (isRemoving) {
        newFavorites = normalizedPrev.filter(id => id !== agentId);
      } else {
        if (normalizedPrev.length >= 6) {
          newFavorites = [...normalizedPrev.slice(1), agentId];
        } else {
          newFavorites = [...normalizedPrev, agentId];
        }
      }
      localStorage.setItem('sidekick-favorite-agents', JSON.stringify(newFavorites));
      window.clarity?.('event', isRemoving ? 'agent_unfavorited' : 'agent_favorited');
      window.clarity?.('set', 'agent', agentId);
      return newFavorites;
    });
  };

  // Get favorite agents for display
  const getFavoriteAgentsForDisplay = () => {
    return favoriteAgents
      .map(id => agentsForPicker.find(agent => agent.id === id))
      .filter((agent): agent is Agent => agent !== undefined);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();

    const isM365Handoff = selectedAgent?.id === 'm365_copilot';
    const isHandoff = isM365Handoff;
    if (isLoading) return;
    if (!isHandoff && !inputValue.trim() && selectedFiles.length === 0) return;

    // Speech disabled: if (isListening) stopListening();

    // Capture session context at send time so background execution targets the right chat
    const sendChatId = currentChatId;
    const sendAgent = selectedAgent;

    const originalInput =
      inputValue.trim() ||
      (selectedFiles.length > 0 ? 'Analyze these files:' : '') ||
      (isM365Handoff ? 'Open Microsoft 365 Copilot' : '');
    let messageContent = originalInput;



    const attachedFiles = [...selectedFiles];
    setInputValue('');
    setSelectedFiles([]);
    setSessionLoading(sendChatId, true);

    // Add to command history - use original input for history
    setCommandHistory(prev => {
      const newHistory = [originalInput, ...prev.filter(cmd => cmd !== originalInput)];
      return newHistory.slice(0, 50); // Keep last 50 commands
    });
    setHistoryIndex(-1);

    // Add user message
    let displayContent = originalInput;
    if (attachedFiles.length > 0) {
      const fileNames = attachedFiles.map(file => file.name).join(', ');
      displayContent += `\n\n📎 Attached files: ${fileNames}`;
    }

    const userMessage: Message = {
      id: generateId(),
      content: displayContent,
      role: 'user',
      timestamp: new Date(),
      agent: sendAgent?.id
    };

    updateChatById(sendChatId, chat => ({
      ...chat,
      messages: [...chat.messages, userMessage],
      lastActive: new Date()
    }));

    window.clarity?.('event', 'chat_conversation');
    window.clarity?.('set', 'agent', sendAgent?.id ?? 'gpt');

    // Force scroll to bottom after adding user message
    scrollToBottom(true);

    // Progressive status messages for non-streaming requests (SSE agents get real-time updates)
    const isSSEAgent = sendAgent?.id === 'research' || sendAgent?.id === 'docs' || sendAgent?.id === 'perplexity_research';
    const statusMessages = attachedFiles.length > 0
      ? ['Processing files...', 'Analyzing content...', 'Generating response...']
      : ['Understanding your request...', 'Analyzing context...', 'Generating response...', 'Finishing up...'];
    let statusIndex = 0;
    if (!isSSEAgent) {
      setStreamingStatus(statusMessages[0]);
    }
    const statusInterval = !isSSEAgent ? setInterval(() => {
      statusIndex = Math.min(statusIndex + 1, statusMessages.length - 1);
      setStreamingStatus(statusMessages[statusIndex]);
    }, 3000) : null;

    try {
      // Debug logging
      // console.log('Sending message:', messageContent);
      // console.log('Input value was:', inputValue);
      // console.log('Message content length:', messageContent.length);

      // Get SSO token from auth context
      const ssoToken = auth.user?.access_token || '';
      //console.log('SSO Token length:', ssoToken.length);

      // Call API with file support
      const formData = new FormData();
      formData.append('message', messageContent);
      formData.append('sso_token', ssoToken);
      formData.append('cw_user_auth_details', JSON.stringify(buildCwUserAuthDetails(auth.user)));
      formData.append('chat_session_id', sendChatId);
      formData.append('temperature', settings.temperature.toString());
      formData.append('agent', sendAgent?.id || 'gpt');

      // Send recent conversation history for context
      const chatForHistory = chatSessions.find(c => c.id === sendChatId);
      const recentMessages = (chatForHistory?.messages || []).slice(-10).map(m => ({
        role: m.role,
        content: m.content,
      }));
      formData.append('chat_history', JSON.stringify(recentMessages));

      // Add files to FormData with the expected field name 'files'
      attachedFiles.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('fileCount', attachedFiles.length.toString());

      // Debug: Log the API URL and FormData contents
      const apiUrl = getApiUrl('/chat');
      // console.log('API URL:', apiUrl);
      // console.log('FormData contents:');
      // for (const [key, value] of formData.entries()) {
      //   console.log(`  ${key}:`, value);
      // }

      // Create AbortController for this request
      const controller = new AbortController();
      abortControllersRef.current.set(sendChatId, controller);

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData, // Use FormData instead of JSON for file uploads
        signal: controller.signal,
      });

      // console.log('Response status:', response.status);
      // console.log('Response headers:', [...response.headers.entries()]);

      if (statusInterval) clearInterval(statusInterval);

      if (!response.ok) {
        const errContent = await chatErrorReplyFromResponse(response);
        const assistantMessage: Message = {
          id: generateId(),
          content: errContent,
          role: 'assistant',
          timestamp: new Date(),
          agent: sendAgent?.id,
        };
        updateChatById(sendChatId, chat => {
          const shouldUpdateTitle = chat.title === 'New Chat' && chat.messages.length === 1;
          const newTitle = shouldUpdateTitle
            ? (originalInput.length > 30 ? originalInput.slice(0, 30) + '...' : originalInput)
            : chat.title;
          return {
            ...chat,
            messages: [...chat.messages, assistantMessage],
            lastActive: new Date(),
            title: newTitle,
          };
        });
        if (sendChatId !== currentChatIdRef.current) {
          markSessionUnread(sendChatId);
        } else {
          scrollToBottom(true);
        }
        abortControllersRef.current.delete(sendChatId);
        setSessionLoading(sendChatId, false);
        setStreamingStatus('');
        return;
      }

      // Check if response is SSE stream (research agent)
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/event-stream') && response.body) {
        const sseResult = await readSseChatStream(response.body, (status) => {
          setStreamingStatus(status);
        });
        setStreamingStatus('');
        if (sseResult.usage) recordUsage(sseResult.usage, sseResult.model);

        const assistantMessage: Message = {
          id: generateId(),
          content: sseResult.content || 'Sorry, I encountered an error processing your request.',
          role: 'assistant',
          timestamp: new Date(),
          agent: sendAgent?.id,
          ...(sseResult.langfuseTraceId && { langfuseTraceId: sseResult.langfuseTraceId }),
          ...(sseResult.links.length > 0 && { links: sseResult.links, pages: sseResult.pages }),
        };

        updateChatById(sendChatId, chat => {
          const shouldUpdateTitle = chat.title === 'New Chat' && chat.messages.length === 1;
          const newTitle = shouldUpdateTitle
            ? (originalInput.length > 30 ? originalInput.slice(0, 30) + '...' : originalInput)
            : chat.title;
          return {
            ...chat,
            messages: [...chat.messages, assistantMessage],
            lastActive: new Date(),
            title: newTitle
          };
        });

        // If user navigated away, mark as unread
        if (sendChatId !== currentChatIdRef.current) {
          markSessionUnread(sendChatId);
        } else {
          scrollToBottom(true);
        }

      } else {
        // Standard JSON response for other agents
        const data = await response.json();
        // console.log('Response data:', data);
        // console.log('Using reply content:', data.reply);
        if (typeof data.m365_copilot_url === 'string') {
          const handoff = data.m365_copilot_url.trim();
          if (handoff) {
            const toCopy = messageContent.trim();
            const isEmptyHandoffPlaceholder = toCopy === 'Open Microsoft 365 Copilot';
            let copiedToClipboard = false;
            if (toCopy && !isEmptyHandoffPlaceholder && navigator.clipboard?.writeText) {
              try {
                await navigator.clipboard.writeText(toCopy);
                copiedToClipboard = true;
              } catch (err) {
                console.warn('M365: clipboard copy failed', err);
              }
            }
            try {
              void openExternalLink(handoff);
            } catch {
              /* ignore */
            }
            if (copiedToClipboard) {
              const base = data.reply || 'Sorry, I encountered an error processing your request.';
              data.reply = `${base}\n\n*Your message was copied to the clipboard — switch to the Copilot tab and paste (Ctrl+V or Cmd+V).*`;
            }
          }
        }
        if (data.usage) recordUsage(data.usage, data.model);

        // Add assistant response
        const assistantMessage: Message = {
          id: generateId(),
          content: data.reply || 'Sorry, I encountered an error processing your request.',
          role: 'assistant',
          timestamp: new Date(),
          agent: sendAgent?.id,
          ...(typeof data.langfuse_trace_id === 'string' &&
            data.langfuse_trace_id.trim() && { langfuseTraceId: data.langfuse_trace_id.trim() }),
          // Include interactive steps if provided
          ...(data.interactive_steps && { interactive_steps: data.interactive_steps }),
          ...excelFieldsFromReply(data),
        };

        updateChatById(sendChatId, chat => {
          // Update title if it's still "New Chat" and this is the first user message
          const shouldUpdateTitle = chat.title === 'New Chat' && chat.messages.length === 1;
          const newTitle = shouldUpdateTitle
            ? (originalInput.length > 30 ? originalInput.slice(0, 30) + '...' : originalInput)
            : chat.title;

          return {
            ...chat,
            messages: [...chat.messages, assistantMessage],
            lastActive: new Date(),
            title: newTitle
          };
        });

        // If user navigated away, mark as unread
        if (sendChatId !== currentChatIdRef.current) {
          markSessionUnread(sendChatId);
        } else {
          scrollToBottom(true);
        }
      }

    } catch (error: any) {
      if (statusInterval) clearInterval(statusInterval);
      if (error.name === 'AbortError') {
        console.log('Request aborted by user');
        const abortedMessage: Message = {
          id: generateId(),
          content: '*Request stopped by user.*',
          role: 'assistant',
          timestamp: new Date(),
          agent: sendAgent?.id
        };
        updateChatById(sendChatId, chat => ({
          ...chat,
          messages: [...chat.messages, abortedMessage],
          lastActive: new Date()
        }));
      } else {
        console.error('Error sending message:', error);

        // Add error message
        const errorMessage: Message = {
          id: generateId(),
          content: 'Sorry, I encountered an error while processing your request. Please try again.',
          role: 'assistant',
          timestamp: new Date(),
          agent: sendAgent?.id
        };

        updateChatById(sendChatId, chat => ({
          ...chat,
          messages: [...chat.messages, errorMessage],
          lastActive: new Date()
        }));
      }

      // If user navigated away, mark as unread; otherwise scroll
      if (sendChatId !== currentChatIdRef.current) {
        markSessionUnread(sendChatId);
      } else {
        scrollToBottom(true);
      }
    }

    abortControllersRef.current.delete(sendChatId);
    setSessionLoading(sendChatId, false);
    setStreamingStatus('');
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyIndex < commandHistory.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setInputValue(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInputValue(commandHistory[newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInputValue('');
      }
    }
  };

  const handleFeedback = (messageId: string, feedback: 'positive' | 'negative') => {
    const messages = getCurrentMessages();
    const message = messages.find(msg => msg.id === messageId);
    updateCurrentChat(chat => ({
      ...chat,
      messages: chat.messages.map(msg =>
        msg.id === messageId ? { ...msg, feedback } : msg
      )
    }));
    window.clarity?.('event', 'message_feedback');
    window.clarity?.('set', 'feedback_type', feedback);
    window.clarity?.('set', 'agent', selectedAgent?.id ?? 'gpt');

    const traceId = message?.langfuseTraceId;
    const token = authRef.current.user?.access_token;
    if (traceId && token) {
      void fetch(getApiUrl('/api/feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          trace_id: traceId,
          value: feedback === 'positive' ? 1 : 0,
          name: 'user_feedback',
          agent_id: message?.agent || selectedAgent?.id || 'gpt',
        }),
      }).catch((err) => {
        console.warn('Langfuse feedback failed', err);
      });
    }
  };

  const handleRetry = (messageId: string) => {
    const messages = getCurrentMessages();
    const messageIndex = messages.findIndex(msg => msg.id === messageId);

    if (messageIndex > 0) {
      const userMessage = messages[messageIndex - 1];
      if (userMessage.role === 'user') {
        setInputValue(userMessage.content);
        inputRef.current?.focus({ preventScroll: true });
      }
    }
    window.clarity?.('event', 'message_retried');
    window.clarity?.('set', 'agent', selectedAgent?.id ?? 'gpt');
  };

  const handlePdfViewerClose = useCallback(() => {
    setPdfViewerOpen(false);
    setPdfDocument('');
    setInitialPage(1);
    setPdfViewerOpen(false);
  }, []);

  // Opens a AIHS source document linked in an AIHS agent response.
  // If the link ends with .pdf → call the PDF viewer component; endpoint to be implimented in the backend.
  const handleAihsSourceDocument = (link: string, page?: number) => {
    const isPdf = link.toLowerCase().endsWith('.pdf');
    window.clarity?.('event', 'source_link_opened');
    window.clarity?.('set', 'source_link_type', isPdf ? 'pdf' : 'web');

    if (isPdf) {
      // Always use the in-app PDF viewer regardless of Teams vs browser context.
      setPdfDocument(link);
      setInitialPage(page || 1);
      setPdfViewerOpen(true);
      return;
    }

    // Non-PDF links: extract an embedded http/https URL if the string isn't already one,
    // then hand off to openExternalLink which handles Teams desktop vs browser correctly.
    const isDirectUrl = /^https?:\/\//i.test(link.trim());
    if (!isDirectUrl) {
      const extracted = link.match(/https?:\/\/[^\s"'>)]+/i);
      if (extracted) {
        void openExternalLink(extracted[0]);
        return;
      }
    }
    void openExternalLink(link);
  };

  // Modal components
  const CommandsHelpModal = () => {
    if (!showHelp) return null;

    return createPortal(
      <div
        className={`fixed inset-0 flex items-center justify-center z-[9999] overflow-y-auto ${settings.theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/30'}`}
        onMouseDown={(e) => { if (e.target === e.currentTarget) setShowHelp(false); }}
      >
        <div
          className={`rounded-lg w-[600px] max-h-[80vh] overflow-y-auto border ${settings.theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300 shadow-xl'}`}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-semibold ${themeClasses.text.primary} flex items-center`}>
                <svg className="w-6 h-6 mr-3 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Help
              </h2>
              <button
                onClick={() => setShowHelp(false)}
                className={`p-1 rounded transition-colors ${themeClasses.bg.hover}`}
              >
                <svg className={`w-5 h-5 ${themeClasses.text.tertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className={`mb-4 p-4 rounded-lg border ${settings.theme === 'dark' ? 'bg-gray-900 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`${themeClasses.text.secondary} text-sm`}>
                Ask questions naturally and the AI assistant will route them to the most appropriate service.
                Select an agent from the sidebar to specialize your queries.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className={`text-sm font-semibold text-cyan-400 mb-2`}>Available Agents</h3>
                <ul className={`text-sm ${themeClasses.text.secondary} space-y-1 ml-4`}>
                  <li><strong>ConnectWise GPT</strong> &mdash; General-purpose AI assistant (default)</li>
                  <li><strong>ConnectWise University</strong> &mdash; Search documentation and knowledge base</li>
                  <li><strong>Web Research</strong> &mdash; Search the public internet, read web pages, and cite sources</li>
                </ul>
              </div>
              <div>
                <h3 className={`text-sm font-semibold text-purple-400 mb-2`}>Tips</h3>
                <ul className={`text-xs ${themeClasses.text.tertiary} space-y-1 ml-4`}>
                  <li>&bull; Type your question directly in the chat box</li>
                  <li>&bull; Use &ldquo;steps&rdquo; or &ldquo;walk me through&rdquo; for step-by-step guides</li>
                  <li>&bull; Switch tools using the sidebar to specialize your queries</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const SettingsModal = () => {
    if (!showSettings) return null;

    return createPortal(
      <div
        className={`fixed inset-0 ${settings.theme === 'dark' ? 'bg-black bg-opacity-50' : 'bg-gray-900 bg-opacity-30'} flex items-center justify-center z-[9999] overflow-y-auto`}
        onMouseDown={(e) => { if (e.target === e.currentTarget) setShowSettings(false); }}
      >
        <div
          className={`${themeClasses.bg.secondary} rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto shadow-xl`}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-xl font-semibold ${themeClasses.text.primary} flex items-center`}>
              <svg className="w-6 h-6 mr-2 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </h2>
            <button
              onClick={() => setShowSettings(false)}
              className={`p-2 rounded-lg transition-colors ${themeClasses.bg.hover}`}
            >
              <svg className={`w-5 h-5 ${themeClasses.text.tertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* AI Configuration */}
            <div className={`border-b ${themeClasses.border.primary} pb-4`}>
              <h3 className={`text-sm font-semibold ${themeClasses.text.secondary} mb-3`}>AI Configuration</h3>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text.secondary} mb-2`}>Model</label>
                  <div
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${settings.theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-300'
                      : 'bg-gray-100 border-gray-300 text-gray-700'
                      } cursor-not-allowed opacity-80`}
                    aria-disabled="true"
                  >
                    {clientChatConfig?.main_chat_model_display || '—'}
                  </div>
                  <p className={`mt-1 text-xs ${themeClasses.text.tertiary}`}>
                    Configured on the server; model selection is not available in the app yet.
                  </p>
                </div>


              </div>
            </div>

            {/* Appearance */}
            <div className={`border-b ${themeClasses.border.primary} pb-4`}>
              <h3 className={`text-sm font-semibold ${themeClasses.text.secondary} mb-3`}>Appearance</h3>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text.secondary} mb-2`}>Theme</label>
                  <CustomSelect
                    value={settings.theme}
                    onChange={(v) => {
                      saveSettings({ ...settings, theme: v as 'dark' | 'light' });
                      window.clarity?.('event', 'theme_changed');
                      window.clarity?.('set', 'theme', v);
                    }}
                    theme={settings.theme}
                    options={[
                      { value: 'dark', label: 'Dark' },
                      { value: 'light', label: 'Light' },
                    ]}
                  />
                </div>


                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text.secondary} mb-2`}>Font Size</label>
                  <CustomSelect
                    value={settings.fontSize}
                    onChange={(v) => saveSettings({ ...settings, fontSize: v as 'small' | 'medium' | 'large' })}
                    theme={settings.theme}
                    options={[
                      { value: 'small', label: 'Small' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'large', label: 'Large' },
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Chat Behavior */}
            <div className={`border-b ${themeClasses.border.primary} pb-4`}>
              <h3 className={`text-sm font-semibold ${themeClasses.text.secondary} mb-3`}>Chat Behavior</h3>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.showMessageTimestamps}
                    onChange={(e) => saveSettings({ ...settings, showMessageTimestamps: e.target.checked })}
                    className={`rounded ${settings.theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} text-cyan-600 focus:ring-cyan-600 ${settings.theme === 'dark' ? 'focus:ring-offset-gray-800' : 'focus:ring-offset-white'}`}
                  />
                  <span className={`ml-3 text-sm ${themeClasses.text.secondary}`}>Show message timestamps</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.startWithCollapsedSidebar}
                    onChange={(e) => saveSettings({ ...settings, startWithCollapsedSidebar: e.target.checked })}
                    className={`rounded ${settings.theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} text-cyan-600 focus:ring-cyan-600 ${settings.theme === 'dark' ? 'focus:ring-offset-gray-800' : 'focus:ring-offset-white'}`}
                  />
                  <span className={`ml-3 text-sm ${themeClasses.text.secondary}`}>Start with collapsed sidebar</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.startInFullscreenMode}
                    onChange={(e) => saveSettings({ ...settings, startInFullscreenMode: e.target.checked })}
                    className={`rounded ${settings.theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} text-cyan-600 focus:ring-cyan-600 ${settings.theme === 'dark' ? 'focus:ring-offset-gray-800' : 'focus:ring-offset-white'}`}
                  />
                  <span className={`ml-3 text-sm ${themeClasses.text.secondary}`}>Start in Fullscreen mode</span>
                </label>
              </div>
            </div>

            {/* Data Management */}
            <div className={`border-b ${themeClasses.border.primary} pb-4`}>
              <h3 className={`text-sm font-semibold ${themeClasses.text.secondary} mb-3`}>Data Management</h3>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text.secondary} mb-2`}>
                    Chat Retention: {settings.chatRetentionDays} days
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="365"
                    step="1"
                    value={settings.chatRetentionDays}
                    onChange={(e) => saveSettings({ ...settings, chatRetentionDays: parseInt(e.target.value) })}
                    className={`w-full h-2 ${themeClasses.bg.input} rounded-lg appearance-none cursor-pointer slider`}
                  />
                  <div className={`flex justify-between text-xs ${themeClasses.text.tertiary} mt-1`}>
                    <span>1 day</span>
                    <span>1 year</span>
                  </div>
                  <p className={`text-xs ${themeClasses.text.muted} mt-1`}>
                    Chats older than this (except your most recently active thread) are removed from this browser and their AgentCore Memory session is purged on the server when you use the app or hourly while the tab is open.
                  </p>
                </div>
              </div>
            </div>

          </div>


          <div className={`mt-8 pt-4 border-t ${themeClasses.border.primary} space-y-3`}>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={async () => {
                  if (
                    await confirmAction({
                      title: 'Log out',
                      message: 'Are you sure you want to log out? Your SSO token will be cleared.',
                      confirmLabel: 'Log out',
                      variant: 'danger',
                    })
                  ) {
                    window.clarity?.('event', 'user_logged_out');
                    localStorage.removeItem('dev-sso-token');
                    document.cookie = 'dev-sso-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost; SameSite=Lax';
                    localStorage.removeItem('sidekick-chat-sessions');
                    auth.removeUser();
                    window.location.reload();
                  }
                }}
                className="px-4 py-2 text-orange-400 hover:text-orange-300 transition-colors text-sm border border-orange-400/20 rounded-lg hover:border-orange-300/40"
              >
                Logout
              </button>
              <button
                onClick={async () => {
                  if (
                    await confirmAction({
                      title: 'Clear all chats',
                      message: 'Are you sure you want to delete all chat sessions? This cannot be undone.',
                      confirmLabel: 'Clear all',
                      variant: 'danger',
                    })
                  ) {
                    window.clarity?.('event', 'all_chats_cleared');
                    clearAllChats();
                  }
                }}
                className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors text-sm border border-red-400/20 rounded-lg hover:border-red-300/40"
              >
                Clear All Chats
              </button>
              <button
                onClick={resetSettings}
                className={`px-4 py-2 ${themeClasses.text.tertiary} ${settings.theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'} transition-colors text-sm border ${themeClasses.border.secondary} rounded-lg`}
              >
                Reset to Defaults
              </button>
            </div>
            <div className="flex justify-between items-center">
              {/* Support Info — bottom left */}
              <div>
                <button
                  type="button"
                  onClick={() => setSupportInfoOpen(v => !v)}
                  className={`text-xs ${themeClasses.text.muted} underline hover:no-underline`}
                >
                  Support Info
                </button>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowSettings(false)}
                  className={`px-4 py-2 ${themeClasses.text.tertiary} ${settings.theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'} transition-colors text-sm border ${themeClasses.border.secondary} rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors text-sm"
                >
                  Close
                </button>
              </div>
            </div>

            {supportInfoOpen && (
              <div className={`pt-3 border-t ${themeClasses.border.primary}`}>
                <TeamsDebugPanel theme={settings.theme} />
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const containerStyle = isFullscreen ? {
    height: '100vh',
    width: '100vw',
    boxShadow: 'none',
    background: settings.theme === 'dark'
      ? 'linear-gradient(90deg, #0099FF 0%, #47C7B2 50%, #C5E654 100%)'
      : '#ffffff',
    padding: '1px',
    borderRadius: '0px',
    position: 'fixed' as const,
    top: 0,
    left: 0,
    zIndex: 9999,
    transition: 'all 0.3s ease-in-out'
  } : {
    height: isMobile() ? 'calc(100vh - 2rem)' : 'min(964px, calc(100vh - 2rem))',
    width: isMobile() ? 'calc(100vw - 1rem)' : 'min(1200px, calc(100vw - 2rem))',
    maxWidth: isMobile() ? '100vw' : 'min(1200px, calc(100vw - 2rem))',
    boxShadow: isMobile() ? '0px 2px 8px 0px #0099FF' : '0px 4px 16px 0px #0099FF',
    background: 'linear-gradient(90deg, #0099FF 0%, #47C7B2 50%, #C5E654 100%)',
    padding: '1px',
    borderRadius: isMobile() ? '4px' : '8px',
    transition: 'all 0.3s ease-in-out'
  };

  const sidebarStyle = {
    backgroundColor: settings.theme === 'dark' ? '#1F2849' : '#f8fafc'
  };

  return (
    <div
      className={isFullscreen
        ? "h-screen overflow-hidden"
        : `h-screen overflow-hidden flex items-center justify-center ${isCompactViewport ? 'p-2' : 'p-8'}`
      }
      style={{ backgroundColor: '#0A1023' }}
    >
      {/* Centered Chatbot Container */}
      <div
        className="flex overflow-hidden h-full"
        style={{
          ...containerStyle,
          height: isFullscreen ? '100vh' : (isCompactViewport ? 'calc(100vh - 1rem)' : 'calc(100vh - 4rem)')
        }}
      >
        <div className={`${themeClasses.bg.primary} flex overflow-hidden w-full h-full rounded-md relative`}>
          {/* Mobile Sidebar Overlay */}
          {isMobile() && !sidebarCollapsed && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setSidebarCollapsed(true)}
            />
          )}

          {/* Sidebar */}
          <div
            className={`${sidebarCollapsed
              ? (isMobile() ? 'w-0 overflow-hidden' : 'w-16')
              : (isMobile() ? 'w-64 fixed left-0 top-0 h-full z-50' : (isCompactViewport ? 'w-64' : 'w-80'))
              } flex flex-col min-h-0 overflow-hidden transition-all duration-300 flex-shrink-0 ${isMobile() && !sidebarCollapsed ? 'shadow-2xl' : ''
              }`}
            style={sidebarStyle}
          >
            {/* Sidebar Header */}
            <div className={`${isCompactViewport ? 'h-14' : 'h-[72px]'} px-4 flex items-center border-b ${themeClasses.border.primary}`}>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-3">
                  {sidebarCollapsed ? (
                    <button
                      onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                      className={`w-8 h-8 ${themeClasses.bg.hover} transition-colors flex items-center justify-center`}
                    >
                      <svg className={`w-5 h-5 ${themeClasses.text.primary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                      className={`p-1.5 ${themeClasses.bg.hover} transition-colors`}
                    >
                      <svg className={`w-5 h-5 ${themeClasses.text.primary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>
                  )}
                </div>

              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 min-h-0 overflow-hidden p-4 flex flex-col">
              {/* New Chat Button */}
              <div className="mb-4">
                <button
                  onClick={createNewChat}
                  className={`w-full ${sidebarCollapsed ? 'justify-center' : 'pr-3 justify-start'} py-2 transition-colors flex items-center ${sidebarCollapsed ? '' : 'space-x-2'} border-b ${themeClasses.border.primary} ${themeClasses.bg.hover}`}
                >
                  <img
                    src="/chat-icon.svg"
                    alt=""
                    className="w-12 h-12"
                  />
                  {!sidebarCollapsed && <span className={`text-sm ${themeClasses.text.primary} font-medium`}>New Chat</span>}
                </button>

                {/* Recent Chats icon - collapsed sidebar */}
                {sidebarCollapsed && (
                  <button
                    onClick={() => setSidebarCollapsed(false)}
                    className={`w-full justify-center py-2 flex items-center transition-colors ${themeClasses.bg.hover}`}
                    title="Recent Chats"
                  >
                    <svg className={`w-5 h-5 ${themeClasses.text.secondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Chat List - Limited to half height */}
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                {!sidebarCollapsed && (
                  <>
                    {/* Recent Chats Section - shares available space equally with Favorite Tools */}
                    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                      <h3 className={`text-xs font-semibold ${themeClasses.text.tertiary} mb-2 px-2 flex-shrink-0`}>Recent Chats</h3>
                      <div
                        className="space-y-1 overflow-y-auto pr-2 flex-1 min-h-0"
                        style={{
                          scrollbarWidth: 'thin',
                          scrollbarColor: settings.theme === 'dark' ? '#374151 #1f2937' : '#d1d5db #f3f4f6'
                        }}
                      >
                        {chatSessions.map((chat) => (
                          <div
                            key={chat.id}
                            className={`w-full px-3 ${isCompactViewport ? 'py-1.5' : 'py-2'} transition-colors cursor-pointer rounded-lg ${currentChatId === chat.id
                              ? themeClasses.text.primary
                              : `${themeClasses.text.secondary} hover:${themeClasses.text.primary}`
                              } ${currentChatId === chat.id
                                ? settings.theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
                                : 'hover:' + (settings.theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100')
                              }`}
                          >
                            {editingChatId === chat.id ? (
                              <div className="space-y-1">
                                <input
                                  ref={titleEditRef}
                                  type="text"
                                  value={editingTitle}
                                  onChange={(e) => setEditingTitle(e.target.value)}
                                  onKeyDown={handleTitleKeyPress}
                                  onBlur={saveTitle}
                                  className={`w-full text-sm font-medium ${themeClasses.bg.input} ${themeClasses.text.primary} rounded px-2 py-1 outline-none focus:ring-2 focus:ring-cyan-400 border ${themeClasses.border.primary}`}
                                  placeholder="Chat title..."
                                />
                                <div className={`text-xs ${themeClasses.text.tertiary}`}>
                                  Press Enter to save, Esc to cancel
                                </div>
                              </div>
                            ) : (
                              <div
                                className="cursor-pointer"
                                onClick={() => {
                                  setCurrentChatId(chat.id);
                                  clearSessionUnread(chat.id);
                                  // Restore the last tool used in this chat, default to ConnectWise GPT
                                  const lastAgentMessage = [...chat.messages].reverse().find(m => m.role === 'assistant' && m.agent);
                                  setSelectedAgent(resolvePickerAgent(lastAgentMessage?.agent || null));
                                }}
                              >
                                <div className="flex items-center justify-between group">
                                  {(() => {
                                    const firstAssistant = chat.messages.find(m => m.role === 'assistant' && m.agent);
                                    const chatAgent = firstAssistant?.agent ? AVAILABLE_AGENTS.find(a => a.id === firstAssistant.agent) : null;
                                    const displayAgent = chatAgent || AVAILABLE_AGENTS.find(a => a.id === 'gpt');
                                    return displayAgent ? (
                                      <div className={`w-5 h-5 rounded-full ${getAgentGradient(displayAgent.id)} flex items-center justify-center flex-shrink-0 mr-2`}>
                                        <displayAgent.icon className="w-3 h-3 text-white" />
                                      </div>
                                    ) : null;
                                  })()}
                                  <div
                                    className="truncate text-sm font-medium flex-1"
                                    onDoubleClick={() => startEditingTitle(chat.id, chat.title)}
                                  >
                                    {chat.title}
                                  </div>
                                  {/* Session status indicators */}
                                  {loadingSessions.has(chat.id) && currentChatId !== chat.id && (
                                    <span className="flex-shrink-0 ml-1" title="Running...">
                                      <svg className="w-3 h-3 text-cyan-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                      </svg>
                                    </span>
                                  )}
                                  {unreadSessions.has(chat.id) && currentChatId !== chat.id && (
                                    <span className="flex-shrink-0 ml-1 w-2 h-2 rounded-full bg-cyan-400" title="New response" />
                                  )}
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        startEditingTitle(chat.id, chat.title);
                                      }}
                                      className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${settings.theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-300'}`}
                                      title="Edit title"
                                    >
                                      <svg className={`w-3 h-3 ${themeClasses.text.tertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (
                                          await confirmAction({
                                            title: 'Delete chat',
                                            message: 'Are you sure you want to delete this chat?',
                                            confirmLabel: 'Delete',
                                            variant: 'danger',
                                          })
                                        ) {
                                          deleteChat(chat.id);
                                        }
                                      }}
                                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-600 transition-all"
                                      title="Delete chat"
                                    >
                                      <svg className={`w-3 h-3 ${themeClasses.text.tertiary} hover:text-white`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                                {!isCompactViewport && (
                                  <div className={`text-xs ${themeClasses.text.tertiary}`}>
                                    {formatRelativeTime(chat.lastActive)} • {chat.messages.length} messages
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Agents Section - shares available space equally with Recent Chats */}
                    <div className={`border-t ${themeClasses.border.primary} pt-4 pb-2 flex-1 min-h-0 flex flex-col overflow-hidden`}>
                      <h3 className={`text-xs font-semibold ${themeClasses.text.tertiary} mb-3 px-2 flex-shrink-0`}>
                        Favorite Tools
                      </h3>
                      <div
                        className="space-y-2 overflow-y-auto pr-2 flex-1 min-h-0"
                        style={{
                          scrollbarWidth: 'thin',
                          scrollbarColor: settings.theme === 'dark' ? '#374151 #1f2937' : '#d1d5db #f3f4f6'
                        }}
                      >
                        {getFavoriteAgentsForDisplay().map((agent) => (
                          <button
                            key={agent.id}
                            onClick={() => {
                              chooseAgent(agent);
                              inputRef.current?.focus({ preventScroll: true });
                            }}
                            className={`w-full text-left px-3 ${isCompactViewport ? 'py-1.5' : 'py-2'} rounded transition-colors ${selectedAgent?.id === agent.id
                              ? 'bg-gray-700 text-white border border-cyan-400/30'
                              : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                              }`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`${isCompactViewport ? 'w-6 h-6' : 'w-8 h-8'} rounded-full flex items-center justify-center flex-shrink-0 ${getAgentGradient(agent.id)}`}>
                                <agent.icon className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{agent.name}</div>
                                {!isCompactViewport && (
                                  <div className="text-xs text-gray-400 truncate">{agent.description}</div>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}

                        {/* Browse All Tools Button */}
                        <button
                          onClick={() => {
                            setShowAllAgents(true);
                            window.clarity?.('event', 'browse_all_tools_opened');
                          }}
                          className={`w-full text-left px-3 ${isCompactViewport ? 'py-1.5' : 'py-2'} rounded transition-colors text-gray-400 hover:text-cyan-400 hover:bg-gray-700/50 border border-gray-600 border-dashed`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`${isCompactViewport ? 'w-6 h-6' : 'w-8 h-8'} rounded-full border-2 border-gray-500 border-dashed flex items-center justify-center flex-shrink-0`}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium">Browse All Tools</div>
                              {!isCompactViewport && (
                                <div className="text-xs text-gray-500">Click stars to manage favorites</div>
                              )}
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Collapsed sidebar icons - tools at bottom */}
                {sidebarCollapsed && (
                  <div className="flex flex-col items-center space-y-3 mt-auto">

                    {/* Favorite Tools icons */}
                    {getFavoriteAgentsForDisplay().map((agent) => (
                      <button
                        key={agent.id}
                        onClick={() => {
                          chooseAgent(agent);
                          inputRef.current?.focus({ preventScroll: true });
                        }}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${selectedAgent?.id === agent.id
                          ? 'bg-gray-700 border border-cyan-400/30'
                          : `${themeClasses.bg.hover}`
                          }`}
                        title={agent.name}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getAgentGradient(agent.id)}`}>
                          <agent.icon className="w-4 h-4 text-white" />
                        </div>
                      </button>
                    ))}

                    {/* Browse All Tools icon */}
                    <button
                      onClick={() => {
                        setShowAllAgents(true);
                        window.clarity?.('event', 'browse_all_tools_opened');
                      }}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors text-gray-400 hover:text-cyan-400 ${themeClasses.bg.hover} border border-gray-600 border-dashed`}
                      title="Browse All Tools"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>

          {/* Main Chat Area */}
          <div className={`flex-1 flex flex-col overflow-hidden ${themeClasses.bg.primary}`}>
            {/* Chat Header */}
            <div className={`${isCompactViewport ? 'h-14' : 'h-[72px]'} flex items-center justify-between px-4 border-b ${themeClasses.border.primary}`}>
              <div className="flex items-center space-x-3">
                {/* Mobile Menu Button */}
                {isMobile() && sidebarCollapsed && (
                  <button
                    onClick={() => setSidebarCollapsed(false)}
                    className={`p-2 rounded-lg transition-colors ${themeClasses.bg.hover}`}
                    title="Open Menu"
                  >
                    <svg className={`w-5 h-5 ${themeClasses.text.primary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                )}

                <div className="flex items-center space-x-2">
                  <AIAssistantLogo size={32} />
                  <div>
                    <h2 className={`text-sm font-medium ${themeClasses.text.primary}`}>ConnectWise AI Assistant</h2>
                    <div className="flex items-center gap-2">
                      <p className={`text-xs ${themeClasses.text.tertiary}`}>{selectedAgent?.name || 'ConnectWise GPT'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPromptGallery(true);
                    window.clarity?.('event', 'prompt_gallery_opened');
                  }}
                  className={`p-2 rounded-lg transition-colors ${themeClasses.bg.hover}`}
                  title="CW AI Prompt Gallery"
                >
                  <svg className={`w-4 h-4 ${themeClasses.text.tertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>

                <button
                  onClick={toggleFullscreen}
                  className={`p-2 rounded-lg transition-colors ${themeClasses.bg.hover}`}
                  title={isFullscreen ? "Exit Fullscreen (Ctrl+F)" : "Enter Fullscreen (Ctrl+F)"}
                >
                  <svg className={`w-4 h-4 ${themeClasses.text.tertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isFullscreen ? (
                      // Exit fullscreen icon
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9h4.5M15 9V4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15h4.5M15 15v4.5m0-4.5l5.5 5.5" />
                    ) : (
                      // Enter fullscreen icon
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    )}
                  </svg>
                </button>

                <button
                  onClick={() => {
                    setShowSettings(true);
                    window.clarity?.('event', 'settings_opened');
                  }}
                  className={`p-2 rounded-lg transition-colors ${themeClasses.bg.hover}`}
                  title="Settings"
                >
                  <svg className={`w-4 h-4 ${themeClasses.text.tertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>

                <button
                  onClick={() => setShowHelp(true)}
                  className={`p-2 rounded-lg transition-colors ${themeClasses.bg.hover}`}
                  title="Help"
                >
                  <svg className={`w-4 h-4 ${themeClasses.text.tertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
            </div>

            {selectedAgent?.id === 'prospecting' && (
              <div className={`flex items-center justify-between gap-3 px-4 py-2 border-b ${themeClasses.border.primary}`}>
                <p className={`text-xs ${zoomInfoError ? 'text-amber-300' : themeClasses.text.tertiary}`}>
                  {zoomInfoError
                    || (zoomInfoConnected
                      ? 'ZoomInfo is connected with your seller sign-in. Search by company name.'
                      : 'The first Prospecting selection opens ZoomInfo once. Later selections stay in this chat.')}
                </p>
                {zoomInfoError && (
                  <button
                    type="button"
                    onClick={() => connectZoomInfo()}
                    className="shrink-0 rounded-md bg-cyan-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
                  >
                    Try ZoomInfo sign-in again
                  </button>
                )}
              </div>
            )}

            {/* Messages Area with Input Form */}
            <div className={`flex-1 flex flex-col relative min-h-0 ${settings.theme === 'dark' ? 'messages-glass-background' : 'messages-light-background'}`}>
              <div
                className={`relative z-10 flex-1 p-4 space-y-4 min-h-0 ${settings.theme === 'dark' ? 'messages-scroll-container' : 'messages-scroll-light'}`}
                ref={messagesContainerRef}
                style={{
                  overflowY: 'scroll',
                  scrollbarWidth: 'thin',
                  scrollbarColor: settings.theme === 'dark' ? '#374151 #1f2937' : '#d1d5db #f3f4f6'
                }}
              >
                {getCurrentMessages().length === 0 ? (
                  <div className="text-center text-gray-400 mt-32">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center p-2">
                      <AIAssistantLogo size={64} />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">ConnectWise AI Assistant</h3>
                    <p className="text-gray-400 mb-6">Start a conversation with an LLM or use the<br></br>quick commands below to search our internal data sources.</p>

                    {loadingSuggestions ? (
                      <div className="space-y-4">
                        <div className="text-center">
                          <div className="animate-pulse">
                            <div className="h-4 bg-gray-700 rounded w-64 mx-auto mb-4"></div>
                            <div className="flex justify-center gap-3">
                              <div className="h-10 bg-gray-700 rounded w-32"></div>
                              <div className="h-10 bg-gray-700 rounded w-32"></div>
                              <div className="h-10 bg-gray-700 rounded w-32"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : smartSuggestions ? (
                      <div className="space-y-4">
                        <div className="text-center">
                          <div className="mb-4 text-gray-300 whitespace-pre-line">
                            {smartSuggestions.greeting}
                          </div>
                        </div>
                        <div className="inline-flex flex-wrap gap-3 justify-center max-w-4xl">
                          {smartSuggestions.suggestions.map((suggestion, index) => {
                            const suggestionText = typeof suggestion === 'string' ? suggestion : suggestion.text;
                            const suggestionAgent = typeof suggestion === 'string' ? null : suggestion.agent;
                            return (
                              <button
                                key={index}
                                onClick={async () => {
                                  const messageContent = suggestionText;
                                  const targetAgent = suggestionAgent
                                    ? agentsForPicker.find(a => a.id === suggestionAgent) || resolvePickerAgent(null)
                                    : selectedAgent;
                                  if (suggestionAgent && targetAgent && targetAgent.id !== selectedAgent?.id) {
                                    setSelectedAgent(targetAgent);
                                  }
                                  window.clarity?.('event', 'smart_suggestion_used');
                                  window.clarity?.('set', 'agent', targetAgent?.id ?? 'gpt');
                                  setSessionLoading(currentChatId, true);

                                  // Add to command history
                                  setCommandHistory(prev => {
                                    const newHistory = [messageContent, ...prev.filter(cmd => cmd !== messageContent)];
                                    return newHistory.slice(0, 50);
                                  });
                                  setHistoryIndex(-1);

                                  // Add user message
                                  const userMessage: Message = {
                                    id: generateId(),
                                    content: messageContent,
                                    role: 'user',
                                    timestamp: new Date(),
                                    agent: targetAgent?.id
                                  };

                                  updateCurrentChat(chat => ({
                                    ...chat,
                                    messages: [...chat.messages, userMessage],
                                    lastActive: new Date()
                                  }));

                                  // Force scroll to bottom after adding user message
                                  scrollToBottom(true);

                                  try {
                                    // Get SSO token from auth context
                                    const ssoToken = auth.user?.access_token || '';

                                    // Create FormData for /api/chat endpoint
                                    const formData = new FormData();
                                    formData.append('message', messageContent);
                                    formData.append('sso_token', ssoToken);
                                    formData.append('cw_user_auth_details', JSON.stringify(buildCwUserAuthDetails(auth.user)));
                                    formData.append('chat_session_id', currentChatId);
                                    formData.append('temperature', settings.temperature.toString());
                                    formData.append('plan_type', 'medium');
                                    formData.append('agent', targetAgent?.id || 'gpt');
                                    formData.append('fileCount', '0');

                                    const apiUrl = getApiUrl('/chat');
                                    const response = await fetch(apiUrl, {
                                      method: 'POST',
                                      body: formData,
                                    });

                                    let replyContent = '';
                                    let interactiveSteps = undefined;
                                    let excelAttachment: { excelBase64?: string; excelFilename?: string } = {};
                                    let sseLinks: ReferenceLinkPayload[] = [];
                                    let ssePages: number[] = [];
                                    const contentType = response.headers.get('content-type') || '';
                                    if (!response.ok) {
                                      replyContent = await chatErrorReplyFromResponse(response);
                                    } else if (contentType.includes('text/event-stream') && response.body) {
                                      const sseResult = await readSseChatStream(response.body, (status) => {
                                        setStreamingStatus(status);
                                      });
                                      setStreamingStatus('');
                                      replyContent = sseResult.content;
                                      sseLinks = sseResult.links;
                                      ssePages = sseResult.pages;
                                      if (sseResult.usage) recordUsage(sseResult.usage, sseResult.model);
                                    } else {
                                      const data = await response.json();
                                      replyContent = data.reply || '';
                                      interactiveSteps = data.interactive_steps;
                                      excelAttachment = excelFieldsFromReply(data);
                                      if (data.usage) recordUsage(data.usage, data.model);
                                    }

                                    // Add assistant response
                                    const assistantMessage: Message = {
                                      id: generateId(),
                                      content: replyContent || 'Sorry, I encountered an error processing your request.',
                                      role: 'assistant',
                                      timestamp: new Date(),
                                      agent: targetAgent?.id,
                                      ...(interactiveSteps && { interactive_steps: interactiveSteps }),
                                      ...(sseLinks.length > 0 && { links: sseLinks, pages: ssePages }),
                                      ...excelAttachment,
                                    };

                                    updateCurrentChat(chat => {
                                      // Update title if it's still "New Chat" and this is the first user message
                                      const shouldUpdateTitle = chat.title === 'New Chat' && chat.messages.length === 1;
                                      const newTitle = shouldUpdateTitle
                                        ? (messageContent.length > 30 ? messageContent.slice(0, 30) + '...' : messageContent)
                                        : chat.title;

                                      return {
                                        ...chat,
                                        messages: [...chat.messages, assistantMessage],
                                        lastActive: new Date(),
                                        title: newTitle
                                      };
                                    });

                                  } catch (error) {
                                    console.error('Error executing suggestion:', error);
                                    const errorMsg = error instanceof Error ? error.message : String(error);
                                    console.error('Full error details:', {
                                      message: errorMsg,
                                      stack: error instanceof Error ? error.stack : undefined,
                                      payload: {
                                        message: messageContent,
                                        main_chat_model: clientChatConfig?.main_chat_model_display,
                                        temperature: settings.temperature,
                                        plan_type: 'medium'
                                      }
                                    });

                                    // Add error message
                                    const errorMessage: Message = {
                                      id: generateId(),
                                      content: `❌ Sorry, there was an error executing your request: ${errorMsg}. Please try again.`,
                                      role: 'assistant',
                                      timestamp: new Date(),
                                      agent: targetAgent?.id
                                    };

                                    updateCurrentChat(chat => {
                                      // Update title if it's still "New Chat" and this is the first user message
                                      const shouldUpdateTitle = chat.title === 'New Chat' && chat.messages.length === 1;
                                      const newTitle = shouldUpdateTitle
                                        ? (messageContent.length > 30 ? messageContent.slice(0, 30) + '...' : messageContent)
                                        : chat.title;

                                      return {
                                        ...chat,
                                        messages: [...chat.messages, errorMessage],
                                        lastActive: new Date(),
                                        title: newTitle
                                      };
                                    });
                                  } finally {
                                    setSessionLoading(currentChatId, false);
                                  }
                                }}
                                className="px-4 py-2 bg-gray-800 text-cyan-400 rounded-lg text-sm border border-gray-700 hover:bg-gray-700 hover:scale-105 transition-all duration-200 cursor-pointer font-medium shadow-sm hover:shadow-md"
                              >
                                <span className="flex items-center gap-2">
                                  {(() => {
                                    const agent = suggestionAgent ? AVAILABLE_AGENTS.find(a => a.id === suggestionAgent) : null;
                                    const AgentIcon = agent?.icon;
                                    return AgentIcon ? <AgentIcon className="w-4 h-4 opacity-70 flex-shrink-0" /> : <span className="text-xs opacity-70">⚡</span>;
                                  })()}
                                  <span>{suggestionText}</span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        <div className="text-center mt-4">
                          <p className="text-xs text-gray-500">
                            {smartSuggestions.personalized ? '🧠' : '💡'} {smartSuggestions.reason}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            ⚡ Click any suggestion to execute it immediately
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="text-center">
                          <p className="text-gray-400 mb-3">Loading personalized suggestions...</p>
                          <p className="text-xs text-gray-400 mb-3">⚡ Click any suggestion to execute it immediately</p>
                          <div className="inline-flex flex-wrap gap-3 justify-center max-w-4xl">
                            {generateSmartSuggestions().map((suggestion, index) => {
                              return (
                                <button
                                  key={index}
                                  onClick={async () => {
                                    const messageContent = suggestion;
                                    window.clarity?.('event', 'smart_suggestion_used');
                                    window.clarity?.('set', 'agent', selectedAgent?.id ?? 'gpt');
                                    setSessionLoading(currentChatId, true);

                                    // Add to command history
                                    setCommandHistory(prev => {
                                      const newHistory = [messageContent, ...prev.filter(cmd => cmd !== messageContent)];
                                      return newHistory.slice(0, 50);
                                    });
                                    setHistoryIndex(-1);

                                    // Add user message
                                    const userMessage: Message = {
                                      id: generateId(),
                                      content: messageContent,
                                      role: 'user',
                                      timestamp: new Date(),
                                      agent: selectedAgent?.id
                                    };

                                    updateCurrentChat(chat => ({
                                      ...chat,
                                      messages: [...chat.messages, userMessage],
                                      lastActive: new Date()
                                    }));

                                    // Force scroll to bottom after adding user message
                                    scrollToBottom(true);

                                    try {
                                      // Get SSO token from auth context
                                      const ssoToken = auth.user?.access_token || '';

                                      // Create FormData for /api/chat endpoint
                                      const formData = new FormData();
                                      formData.append('message', messageContent);
                                      formData.append('sso_token', ssoToken);
                                      formData.append('cw_user_auth_details', JSON.stringify(buildCwUserAuthDetails(auth.user)));
                                      formData.append('chat_session_id', currentChatId);
                                      formData.append('temperature', settings.temperature.toString());
                                      formData.append('plan_type', 'medium');
                                      formData.append('agent', selectedAgent?.id || 'gpt');
                                      formData.append('fileCount', '0');

                                      const apiUrl = getApiUrl('/chat');
                                      const response = await fetch(apiUrl, {
                                        method: 'POST',
                                        body: formData,
                                      });

                                      let replyContent = '';
                                      let interactiveSteps = undefined;
                                      let excelAttachment: { excelBase64?: string; excelFilename?: string } = {};
                                      let sseLinks: ReferenceLinkPayload[] = [];
                                      let ssePages: number[] = [];
                                      const contentType = response.headers.get('content-type') || '';
                                      if (!response.ok) {
                                        replyContent = await chatErrorReplyFromResponse(response);
                                      } else if (contentType.includes('text/event-stream') && response.body) {
                                        const sseResult = await readSseChatStream(response.body, (status) => {
                                          setStreamingStatus(status);
                                        });
                                        setStreamingStatus('');
                                        replyContent = sseResult.content;
                                        sseLinks = sseResult.links;
                                        ssePages = sseResult.pages;
                                        if (sseResult.usage) recordUsage(sseResult.usage, sseResult.model);
                                      } else {
                                        const data = await response.json();
                                        replyContent = data.reply || '';
                                        interactiveSteps = data.interactive_steps;
                                        excelAttachment = excelFieldsFromReply(data);
                                        if (data.usage) recordUsage(data.usage, data.model);
                                      }

                                      // Add assistant response
                                      const assistantMessage: Message = {
                                        id: generateId(),
                                        content: replyContent || 'Sorry, I encountered an error processing your request.',
                                        role: 'assistant',
                                        timestamp: new Date(),
                                        agent: selectedAgent?.id,
                                        ...(interactiveSteps && { interactive_steps: interactiveSteps }),
                                        ...(sseLinks.length > 0 && { links: sseLinks, pages: ssePages }),
                                        ...excelAttachment,
                                      };

                                      updateCurrentChat(chat => {
                                        // Update title if it's still "New Chat" and this is the first user message
                                        const shouldUpdateTitle = chat.title === 'New Chat' && chat.messages.length === 1;
                                        const newTitle = shouldUpdateTitle
                                          ? (messageContent.length > 30 ? messageContent.slice(0, 30) + '...' : messageContent)
                                          : chat.title;

                                        return {
                                          ...chat,
                                          messages: [...chat.messages, assistantMessage],
                                          lastActive: new Date(),
                                          title: newTitle
                                        };
                                      });

                                    } catch (error) {
                                      console.error('Error executing fallback suggestion:', error);
                                      const errorMsg = error instanceof Error ? error.message : String(error);
                                      console.error('Full error details:', {
                                        message: errorMsg,
                                        stack: error instanceof Error ? error.stack : undefined,
                                        payload: {
                                          message: messageContent,
                                          main_chat_model: clientChatConfig?.main_chat_model_display,
                                          temperature: settings.temperature,
                                          plan_type: 'medium'
                                        }
                                      });

                                      // Add error message
                                      const errorMessage: Message = {
                                        id: generateId(),
                                        content: `❌ Sorry, there was an error executing your request: ${errorMsg}. Please try again.`,
                                        role: 'assistant',
                                        timestamp: new Date(),
                                        agent: selectedAgent?.id
                                      };

                                      updateCurrentChat(chat => {
                                        // Update title if it's still "New Chat" and this is the first user message
                                        const shouldUpdateTitle = chat.title === 'New Chat' && chat.messages.length === 1;
                                        const newTitle = shouldUpdateTitle
                                          ? (messageContent.length > 30 ? messageContent.slice(0, 30) + '...' : messageContent)
                                          : chat.title;

                                        return {
                                          ...chat,
                                          messages: [...chat.messages, errorMessage],
                                          lastActive: new Date(),
                                          title: newTitle
                                        };
                                      });
                                    } finally {
                                      setSessionLoading(currentChatId, false);
                                    }
                                  }}
                                  className="px-4 py-2 bg-gray-800 text-cyan-400 rounded-lg text-sm border border-gray-700 hover:bg-gray-700 hover:scale-105 transition-all duration-200 cursor-pointer font-medium shadow-sm hover:shadow-md opacity-75"
                                >
                                  <span className="flex items-center gap-2">
                                    <span className="text-xs opacity-70">⚡</span>
                                    <span>{suggestion}</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {getCurrentMessages().map((message, index) => (
                      <MessageComponent
                        key={index}
                        message={message}
                        onFeedback={handleFeedback}
                        onRetry={handleRetry}
                        onPlanChange={handlePlanChange}
                        onAihsSourceDocument={handleAihsSourceDocument}
                        isStreaming={message.isStreaming}
                        isRegenerating={isLoading}
                        theme={settings.theme}
                        showTimestamps={settings.showMessageTimestamps}
                        isFullscreen={isFullscreen}
                        sidebarCollapsed={sidebarCollapsed}
                      />
                    ))}

                    {isLoading && !getCurrentMessages().some(m => m.isStreaming) && (
                      <div className="flex items-start space-x-3">
                        {selectedAgent ? (
                          <div className={`w-8 h-8 rounded-full ${getAgentGradient(selectedAgent.id)} flex items-center justify-center flex-shrink-0`}>
                            <selectedAgent.icon className="w-4 h-4 text-white" />
                          </div>
                        ) : (
                          <div className={`w-8 h-8 rounded-full ${themeClasses.bg.tertiary} flex items-center justify-center flex-shrink-0`}>
                            <AIAssistantLogo size={20} />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className={`${themeClasses.message.assistant} rounded-lg p-4`}>
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                              <span className={`${themeClasses.text.tertiary} text-sm ml-2 transition-all duration-300`}>
                                {streamingStatus || 'Thinking...'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area - Textarea above buttons */}
              <form onSubmit={handleSend} className={`p-2 ${isMobile() ? 'pb-safe-2' : 'p-4'}`} style={{ paddingLeft: isMobile() ? '0.5rem' : '4rem', paddingRight: isMobile() ? '0.5rem' : undefined }}>
                <div
                  className="chat-composer-shell rounded-lg focus-within:ring-1 focus-within:ring-cyan-400/30 transition-colors"
                  style={{
                    background: settings.theme === 'dark'
                      ? 'linear-gradient(90deg, #0099FF 0%, #47C7B2 50%, #C5E654 100%)'
                      : '#e5e7eb',
                    padding: '1px'
                  }}
                >
                  <div className="rounded-lg w-full h-full" style={{
                    backgroundColor: settings.theme === 'dark' ? '#000000ff' : '#ffffff'
                  }}>
                    {/* Selected Files Display */}
                    {selectedFiles.length > 0 && (
                      <div className={`px-4 pt-3 border-b ${themeClasses.border.primary}`}>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {selectedFiles.map((file, index) => (
                            <div
                              key={index}
                              className={`flex items-center ${settings.theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg px-3 py-2 text-sm`}
                            >
                              <svg className={`w-4 h-4 mr-2 ${themeClasses.text.tertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className={`${themeClasses.text.primary} mr-2`}>{file.name}</span>
                              <span className={`${themeClasses.text.tertiary} mr-2`}>({formatFileSize(file.size)})</span>
                              <button
                                onClick={() => removeFile(index)}
                                className={`${themeClasses.text.tertiary} hover:text-red-400 transition-colors`}
                                title="Remove file"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Speech interim / mic error UI — disabled with Azure Speech
                {(isListening || interimText) && (
                  <div className={`flex items-center ${isMobile() ? 'px-3 pt-2' : 'px-4 pt-3'} text-xs`}>
                    {isListening && (
                      <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse mr-2" />
                    )}
                    <span className={isListening ? 'text-red-400' : themeClasses.text.tertiary}>
                      {interimText || 'Listening…'}
                    </span>
                  </div>
                )}
                {speechError && (
                  <div className={`${isMobile() ? 'px-3 pt-1' : 'px-4 pt-1'} text-xs text-red-400`}>
                    Mic error: {speechError}
                  </div>
                )}
                */}

                    {/* Textarea Field for multiline support - Full width */}
                    <textarea
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        // Allow Shift+Enter for new lines, Enter alone to send
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend(e as any);
                        } else {
                          handleInputKeyDown(e);
                        }
                      }}
                      placeholder={isMobile() ? "Type a message..." : "Type a message (Shift+Enter for new line)"}
                      disabled={isLoading}
                      rows={(isMobile() || isCompactViewport) ? 1 : 3}
                      className={`chat-composer-input w-full bg-transparent ${(isMobile() || isCompactViewport) ? 'px-3 py-2' : 'px-4 py-3'} ${themeClasses.text.primary} ${settings.theme === 'dark' ? 'placeholder-white' : 'placeholder-gray-400'} outline-none ${settings.theme === 'dark' ? 'disabled:text-gray-500' : 'disabled:text-gray-400'} ${(isMobile() || isCompactViewport) ? 'text-base' : 'text-sm'} resize-none overflow-y-auto border-none`}
                      style={{
                        minHeight: (isMobile() || isCompactViewport) ? '36px' : '70px',
                        maxHeight: (isMobile() || isCompactViewport) ? '120px' : '190px',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        fontSize: isMobile() ? '16px' : undefined // Prevents zoom on iOS
                      }}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = 'auto';
                        const maxHeight = (isMobile() || isCompactViewport) ? 120 : 190;
                        target.style.height = `${Math.min(target.scrollHeight, maxHeight)}px`;
                      }}
                    />

                    {/* Button Row */}
                    <div className={`flex items-center justify-between ${isMobile() ? 'px-3 pb-2' : 'px-4 pb-3'}`}>
                      <div className={`flex items-center ${isMobile() ? 'space-x-1' : 'space-x-2'}`}>
                        {/* Plus Button */}
                        <button
                          type="button"
                          onClick={handleFileSelect}
                          className={`${isMobile() ? 'p-3' : 'p-2'} ${themeClasses.text.tertiary} ${settings.theme === 'dark' ? 'hover:text-gray-300' : 'hover:text-gray-600'} transition-colors rounded border ${themeClasses.border.secondary} ${settings.theme === 'dark' ? 'hover:border-gray-500' : 'hover:border-gray-400'} ${isMobile() ? 'touch-manipulation' : ''}`}
                          title="Attach file"
                        >
                          <svg className={`${isMobile() ? 'w-5 h-5' : 'w-4 h-4'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>

                        {/* Hidden file input */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          multiple
                          accept=".txt,.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.svg,.mp3,.mp4,.avi,.mov,.csv,.xlsx,.xls,.ppt,.pptx,.zip,.rar,.json,.xml,.html,.css,.js,.ts,.py,.java,.cpp,.c,.h,.md,.rtf"
                          style={{ display: 'none' }}
                        />
                      </div>

                      <div className={`flex items-center ${isMobile() ? 'space-x-1' : 'space-x-2'}`}>
                        {/* Agent Selector */}
                        <select
                          value={selectedAgent?.id || agentsForPicker[0]?.id || 'gpt'}
                          onChange={(e) => {
                            const agent = agentsForPicker.find(a => a.id === e.target.value);
                            chooseAgent(agent || null);

                            // Focus the input when agent is selected
                            if (agent && inputRef.current) {
                              inputRef.current.focus({ preventScroll: true });
                            }
                          }}
                          className={`bg-transparent ${themeClasses.text.primary} text-sm outline-none cursor-pointer rounded border ${themeClasses.border.secondary} ${settings.theme === 'dark' ? 'hover:border-gray-500' : 'hover:border-gray-400'} ${isMobile() ? 'touch-manipulation' : ''}`}
                          style={{
                            paddingLeft: '12px',
                            paddingRight: '40px',
                            paddingTop: '8px',
                            paddingBottom: '8px'
                          }}
                        >
                          {agentsForPicker.map((agent) => (
                            <option key={agent.id} value={agent.id} className={settings.theme === 'dark' ? 'bg-gray-800' : 'bg-white'}>
                              {agent.name}
                            </option>
                          ))}
                        </select>

                        {/* Voice Input — disabled with Azure Speech
                    {!isMobile() && (
                      <button
                        type="button"
                        onClick={isVoiceInputEnabled ? toggleListening : undefined}
                        disabled={!isVoiceInputEnabled || isLoading}
                        className={`p-2 transition-colors rounded border ${
                          isListening
                            ? 'text-red-400 border-red-500 bg-red-500/10 animate-pulse'
                            : `${themeClasses.text.tertiary} ${themeClasses.border.secondary} ${isVoiceInputEnabled ? (settings.theme === 'dark' ? 'hover:text-gray-300 hover:border-gray-500' : 'hover:text-gray-600 hover:border-gray-400') : ''}`
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={!isVoiceInputEnabled ? 'Voice coming soon' : isListening ? 'Stop listening' : 'Voice input'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                      </button>
                    )}
                    */}

                        {/* Send / Stop Button */}
                        {isLoading ? (
                          <button
                            type="button"
                            onClick={() => {
                              abortControllersRef.current.get(currentChatId)?.abort();
                              window.clarity?.('event', 'request_aborted');
                              window.clarity?.('set', 'agent', selectedAgent?.id ?? 'gpt');
                            }}
                            className={`text-red-400 hover:text-red-300 transition-colors rounded border border-red-600 hover:border-red-500 hover:bg-red-500/10 ${isMobile() ? 'touch-manipulation' : ''}`}
                            style={{ padding: '10px 8px' }}
                            title="Stop request"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="6" y="6" width="12" height="12" rx="2" />
                            </svg>
                          </button>
                        ) : (
                          <button
                            type="submit"
                            disabled={!inputValue.trim() && selectedFiles.length === 0}
                            className={`text-cyan-400 hover:text-cyan-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors rounded border border-gray-600 hover:border-gray-500 ${isMobile() ? 'touch-manipulation' : ''}`}
                            style={{ padding: '10px 8px' }}
                            title="Send message"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Modals - called as functions, not components, to avoid remount on parent re-render */}
      {CommandsHelpModal()}
      {SettingsModal()}
      <PromptGalleryModal
        open={showPromptGallery}
        onClose={() => setShowPromptGallery(false)}
        theme={settings.theme}
        agentsForPicker={agentsForPicker}
        allowedAgentIds={allowedAgentIds}
        accessToken={auth.user?.access_token || ''}
        promptsConfigFetch={clientChatConfigFetch}
        promptsTableConfigured={!!clientChatConfig?.prompts_table_configured}
        onUsePrompt={(body, agentPick) => {
          const full = agentsForPicker.find((a) => a.id === agentPick.id);
          if (full) setSelectedAgent(full);
          setInputValue(body);
          setShowPromptGallery(false);
          window.clarity?.('event', 'prompt_gallery_prompt_used');
          window.clarity?.('set', 'agent', agentPick.id);
        }}
      />

      {/* Browse All Tools Modal */}
      {showAllAgents && createPortal(
        <div className={`fixed inset-0 flex items-center justify-center z-[9999] ${settings.theme === 'dark' ? 'bg-black bg-opacity-50' : 'bg-gray-900 bg-opacity-30'} ${isCompactViewport ? 'p-2' : ''}`} onClick={() => setShowAllAgents(false)}>
          <div
            className={`rounded-lg w-full overflow-hidden flex flex-col ${isCompactViewport ? 'max-w-3xl max-h-[90vh] mx-2' : 'max-w-4xl max-h-[80vh] mx-4'} ${settings.theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow-xl'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`${isCompactViewport ? 'px-4 py-3' : 'p-6'} border-b ${themeClasses.border.primary} flex justify-between items-center flex-shrink-0`}>
              <h2 className={`${isCompactViewport ? 'text-base' : 'text-xl'} font-semibold ${themeClasses.text.primary}`}>All Available Tools</h2>
              <button
                onClick={() => setShowAllAgents(false)}
                className={`transition-colors ${themeClasses.text.tertiary} ${settings.theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}`}
              >
                <svg className={isCompactViewport ? 'w-5 h-5' : 'w-6 h-6'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className={`${isCompactViewport ? 'p-3' : 'p-6'} overflow-y-auto flex-1 min-h-0`}>
              <div className={`grid grid-cols-1 md:grid-cols-2 ${isCompactViewport ? 'gap-2' : 'gap-4'}`}>
                {agentsForPicker.map((agent) => (
                  <div
                    key={agent.id}
                    className={`relative rounded-lg border transition-all group ${isCompactViewport ? 'p-2.5' : 'p-4'} ${settings.theme === 'dark' ? 'border-gray-600 hover:border-cyan-400/50 hover:bg-gray-700/50' : 'border-gray-300 hover:border-cyan-400/50 hover:bg-gray-50'}`}
                  >
                    {/* Favorite Star Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavoriteAgent(agent.id);
                      }}
                      className={`absolute z-10 p-1 rounded-full transition-colors ${isCompactViewport ? 'top-1.5 right-1.5' : 'top-3 right-3'} ${settings.theme === 'dark' ? 'hover:bg-gray-600/50' : 'hover:bg-gray-200/50'}`}
                      title={favoriteAgents.includes(agent.id) ? "Remove from favorites" : "Add to favorites"}
                    >
                      <svg
                        className={`${isCompactViewport ? 'w-4 h-4' : 'w-5 h-5'} transition-colors ${favoriteAgents.includes(agent.id)
                          ? 'text-yellow-400 hover:text-yellow-300'
                          : 'text-gray-400 hover:text-yellow-400'
                          }`}
                        fill={favoriteAgents.includes(agent.id) ? "currentColor" : "none"}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    </button>

                    {/* Agent Content */}
                    <button
                      onClick={() => {
                        chooseAgent(agent);
                        setShowAllAgents(false);
                        inputRef.current?.focus({ preventScroll: true });
                        window.clarity?.('event', 'agent_selected_from_browser');
                      }}
                      className="w-full text-left"
                    >
                      <div className={`flex items-start pr-8 ${isCompactViewport ? 'space-x-2' : 'space-x-3'}`}>
                        <div className={`${isCompactViewport ? 'w-7 h-7' : 'w-10 h-10'} rounded-full flex items-center justify-center flex-shrink-0 ${getAgentGradient(agent.id)}`}>
                          <agent.icon className={`${isCompactViewport ? 'w-3.5 h-3.5' : 'w-5 h-5'} text-white`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h3 className={`${isCompactViewport ? 'text-xs' : 'text-sm'} font-medium truncate ${themeClasses.text.primary}`}>{agent.name}</h3>
                          </div>
                          {!isCompactViewport && (
                            <p className={`text-xs mt-1 ${themeClasses.text.tertiary}`}>{agent.description}</p>
                          )}
                          <div className={`flex flex-wrap gap-1 ${isCompactViewport ? 'mt-1' : 'mt-2'}`}>
                            {agent.capabilities.slice(0, isCompactViewport ? 2 : 3).map((capability, index) => (
                              <span key={index} className={`text-xs rounded ${isCompactViewport ? 'px-1.5 py-0.5' : 'px-2 py-1'} ${settings.theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                                {capability}
                              </span>
                            ))}
                            {agent.capabilities.length > (isCompactViewport ? 2 : 3) && (
                              <span className={`text-xs ${settings.theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>+{agent.capabilities.length - (isCompactViewport ? 2 : 3)} more</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className={`${isCompactViewport ? 'px-3 py-2' : 'p-4'} border-t text-xs text-center flex-shrink-0 ${settings.theme === 'dark' ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-600'}`}>
              Click the ⭐ star to add/remove from favorites • {favoriteAgents.length}/6 favorites selected
            </div>
          </div>
        </div>,
        document.body
      )}

      {confirmDialog}

      <PdfViewer
        pdfName={pdfDocument}
        open={pdfViewerOpen}
        onClose={handlePdfViewerClose}
        initialPage={initialPdfPage}
        accessToken={auth.user?.access_token || ''}
      />

    </div>
  );
};

export default ChatBot;
